import { Children, Fragment } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
    siteBuilderBlockDelegatesOutlineToColumn,
    siteBuilderEditorBlockAttributes,
    siteBuilderEditorChildPath,
    siteBuilderEditorColumnAttributes,
    siteBuilderEditorInlinePlaceholderAttributes,
    siteBuilderEditorNestedBlockAttributes,
    siteBuilderEditorOutlineOwnerAttributes,
    siteBuilderEditorPartAttributes,
    siteBuilderEditorRootPath,
    siteBuilderEditorSlotAttributes,
} from "./builderInstrumentation";
import {
    siteEmbedSource,
    siteMapEmbedSource,
    siteVideoSource,
} from "./embedSources";
import {
    siteBuilderFormConfig,
    siteBuilderUsesAuthoredFormPreview,
} from "./siteBuilderForm";
import { siteBuilderIconGlyph } from "./siteBuilderIcon";
import { siteBuilderButtonClass } from "./siteBuilderTheme";
import {
    SITE_BUILDER_INTERACTION_CSS,
    siteBuilderInteractionAttributes,
    siteBuilderInteractionStyle,
} from "./siteBuilderInteractions";
import {
    siteBuilderAspectRatio,
    siteBuilderAvatarInitials,
    siteBuilderMediaPresentation,
    siteBuilderProgressValue,
    siteBuilderRatingPresentation,
    siteBuilderSearchSuggestions,
} from "./siteBuilderBlockPresentation";
import {
    siteBuilderFieldValue,
} from "./siteBuilderFieldValues";
import {
    SITE_BUILDER_PART_STYLE_KEYS,
    siteBuilderBlockStyle,
    siteBuilderPartOptionKey,
} from "./siteBuilderStyles";
import { SiteBuilderCountdown } from "./SiteBuilderCountdown";
import { BookingPortalBlock } from "./BookingPortalBlock";
import {
    BOOKING_PORTAL_BLOCK_TYPE,
    hasBookingPortalBlock,
    siteHasBookingPortal,
} from "./bookingPortal";
import {
    resolveSiteBuilderRuntimeHref,
    siteBuilderPageHrefToken,
} from "./siteBuilderRuntimeHref";
import { SiteBuilderInteractionRuntime } from "./SiteBuilderInteractionRuntime";
import { SiteBuilderSearchForm } from "./SiteBuilderSearchForm";
import { SiteBuilderShareAnchor } from "./SiteBuilderShareAnchor";
import {
    SiteNavigation,
    SiteNavigationStyles,
} from "./SiteNavigationView";
import { siteBuilderShareNetwork } from "./siteBuilderShare";
import { isSiteNavigationBlock } from "./siteNavigation";
import {
    WEB_FOOTER_EXTENDED_SLOT_KEYS,
    resolveWebFooterVariant,
    siteBuilderBlockStacksOnMobile,
    webFooterExtendedSlotGridItemStyle,
    webFooterFallbackIndex,
    webFooterGridItemPresentationStyle,
    webFooterGridItemStyle,
    webFooterGridRowCount,
    webFooterUsesLayoutSkeleton,
} from "./webFooterLayout";
import type {
    WebFooterExtendedSlotKey,
    WebFooterExtendedSlotState,
} from "./webFooterLayout";
import type {
    SiteBuilderBlock,
    SiteBuilderContentJson,
    SiteBuilderFormRenderer,
    SiteBuilderLinkComponent,
    SiteBuilderRuntimePage,
    SiteBuilderRuntimePageLink,
    SiteBuilderRuntimeSite,
    SiteNavigationMenu,
    ResolvedSiteBuilderDesign,
} from "./siteBuilderTypes";
import {
    resolveWebSiteNavigationModel,
    webSiteNavigationUsesContainerBreakpoint,
} from "./webSiteNavigation";

type SiteBuilderRuntime = {
    bookingDemoData: boolean;
    design: ResolvedSiteBuilderDesign;
    editorMode: boolean;
    headerActions?: ReactNode;
    navigationReadOnly: boolean;
    readOnly?: boolean;
    renderForm?: SiteBuilderFormRenderer;
    LinkComponent: SiteBuilderLinkComponent;
    menus: readonly SiteNavigationMenu[];
    page: SiteBuilderRuntimePage | null;
    pages: SiteBuilderRuntimePageLink[];
    site: SiteBuilderRuntimeSite | null;
};

type StyleRecord = CSSProperties & Record<string, string | number | undefined>;

type NestedBlockEntry = {
    block: SiteBuilderBlock;
    index: number;
    slot: string;
};

type SiteBuilderChartSeries = {
    borderColor: string;
    color: string;
    label: string;
};

type SiteBuilderChartRow = {
    label: string;
    values: number[];
};

const SITE_BUILDER_CHART_COLORS = [
    "var(--site-primary)",
    "var(--site-accent)",
    "color-mix(in srgb, var(--site-primary) 68%, var(--site-accent))",
    "color-mix(in srgb, var(--site-primary) 42%, var(--site-accent))",
    "color-mix(in srgb, var(--site-primary) 76%, var(--site-surface))",
    "color-mix(in srgb, var(--site-accent) 72%, var(--site-surface))",
];

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function textValue(value: unknown, fallback = ""): string {
    return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
    const numeric = typeof value === "number" ? value : Number(value);

    return Number.isFinite(numeric) ? numeric : fallback;
}

function booleanValue(value: unknown): boolean {
    return value === true || value === "true" || value === 1 || value === "1";
}

function optionalBooleanValue(value: unknown): boolean | undefined {
    return value === undefined || value === null || value === ""
        ? undefined
        : booleanValue(value);
}

function parseJsonArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }

    try {
        const parsed = JSON.parse(value) as unknown;

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function blockOption(block: SiteBuilderBlock, key: string): unknown {
    return block.options?.[key];
}

function hasPositiveGridNumberOption(block: SiteBuilderBlock, key: string): boolean {
    const value = Number(blockOption(block, key));

    return Number.isFinite(value) && value > 0;
}

function hasGridLengthOption(block: SiteBuilderBlock, key: string): boolean {
    const rawValue = blockOption(block, key);

    if (typeof rawValue === "number") return Number.isFinite(rawValue) && rawValue > 0;
    if (typeof rawValue !== "string") return false;

    const value = rawValue.trim();
    const numericMatch = value.match(/^(\d+(?:\.\d+)?)(?:px)?$/iu);

    return numericMatch !== null && Number(numericMatch[1]) > 0;
}

function blockOptionText(block: SiteBuilderBlock, key: string, fallback = ""): string {
    return textValue(blockOption(block, key), fallback);
}

function blockText(block: SiteBuilderBlock, key: string, fallback = ""): string {
    return textValue(block.content?.[key], fallback);
}

function blockFieldText(
    block: SiteBuilderBlock,
    scope: "content" | "options",
    key: string,
    fallback = "",
): string {
    return textValue(siteBuilderFieldValue(block, scope, key), fallback);
}

function firstBlockText(block: SiteBuilderBlock, keys: string[], fallback = ""): string {
    for (const key of keys) {
        const value = blockText(block, key);

        if (value !== "") {
            return value;
        }
    }

    return fallback;
}

function ratingGlyph(block: SiteBuilderBlock, active: boolean): ReactNode {
    const mode = blockOptionText(block, "ratingIcon", "stars");

    if (mode === "custom") {
        const icon = blockText(
            block,
            active ? "ratingActiveIcon" : "ratingInactiveIcon",
            "lucide:star",
        );

        return siteBuilderIconGlyph(icon);
    }

    if (mode === "thumbs") {
        return "👍";
    }

    if (mode === "circles") {
        return active ? "●" : "○";
    }

    if (mode === "squares") {
        return active ? "■" : "□";
    }

    if (mode === "hearts") {
        return active ? "♥" : "♡";
    }

    return active ? "★" : "☆";
}

function ratingFontSize(block: SiteBuilderBlock): string {
    const size = blockFieldText(block, "options", "ratingSize", "md");

    if (size === "xs") return "1rem";
    if (size === "sm") return "1.2rem";
    if (size === "lg") return "1.75rem";
    if (size === "xl") return "2.15rem";
    if (/^\d+(?:\.\d+)?(?:px|rem|em|%)?$/u.test(size)) {
        return cssLength(size) ?? "1.45rem";
    }

    return "1.45rem";
}

function chartNumber(value: unknown, fallback = 0): number {
    const numeric = typeof value === "number"
        ? value
        : Number(String(value ?? "").replace(",", "."));

    return Number.isFinite(numeric) ? numeric : fallback;
}

function chartSeries(block: SiteBuilderBlock): SiteBuilderChartSeries[] {
    const parsed = parseJsonArray(block.content?.chartSeries)
        .map((item, index): SiteBuilderChartSeries | null => {
            const itemRecord = record(item);
            const fallback = SITE_BUILDER_CHART_COLORS[index % SITE_BUILDER_CHART_COLORS.length] ?? "var(--site-primary)";
            const label = textValue(itemRecord.label).trim() || `Series ${index + 1}`;
            const color = textValue(itemRecord.color).trim() || fallback;
            const borderColor = textValue(itemRecord.borderColor).trim() || color;

            return Object.keys(itemRecord).length > 0
                ? { borderColor, color, label }
                : null;
        })
        .filter((item): item is SiteBuilderChartSeries => item !== null);

    if (parsed.length > 0) {
        return parsed;
    }

    const color = blockOptionText(block, "chartDatasetColor", SITE_BUILDER_CHART_COLORS[0]);

    return [{
        borderColor: blockOptionText(block, "chartDatasetBorder", color),
        color,
        label: "Series 1",
    }];
}

function chartRows(
    block: SiteBuilderBlock,
    seriesCount: number,
): SiteBuilderChartRow[] {
    const parsed = parseJsonArray(block.content?.chartRows)
        .map((item): SiteBuilderChartRow | null => {
            const itemRecord = record(item);
            const values = Array.isArray(itemRecord.values) ? itemRecord.values : [];

            return Object.keys(itemRecord).length > 0
                ? {
                    label: textValue(itemRecord.label).trim() || "Item",
                    values: Array.from(
                        { length: seriesCount },
                        (_, index) => chartNumber(values[index]),
                    ),
                }
                : null;
        })
        .filter((item): item is SiteBuilderChartRow => item !== null);

    if (parsed.length > 0) {
        return parsed;
    }

    const legacyRows = firstBlockText(block, ["items"])
        .split("\n")
        .filter(Boolean)
        .map((item) => {
            const [label, rawValue = "0"] = item.split(":");

            return {
                label: label.trim() || "Item",
                values: Array.from(
                    { length: seriesCount },
                    (_, index) => index === 0 ? chartNumber(rawValue) : 0,
                ),
            };
        });

    return legacyRows.length > 0
        ? legacyRows
        : [
            { label: "First", values: [12] },
            { label: "Second", values: [24] },
            { label: "Third", values: [18] },
            { label: "Fourth", values: [17] },
            { label: "Fifth", values: [10] },
        ].map((row) => ({
            label: row.label,
            values: Array.from(
                { length: seriesCount },
                (_, index) => row.values[index] ?? 0,
            ),
        }));
}

function chartBounds(
    block: SiteBuilderBlock,
    rows: SiteBuilderChartRow[],
): { max: number; min: number } {
    const values = rows.flatMap((row) => row.values);
    const configuredMin = chartNumber(blockOption(block, "chartMinAxis"), Number.NaN);
    const configuredMax = chartNumber(blockOption(block, "chartMaxAxis"), Number.NaN);
    const min = Number.isFinite(configuredMin)
        ? configuredMin
        : Math.min(0, ...values);
    const dataMax = Math.max(1, ...values);
    const max = Number.isFinite(configuredMax)
        ? configuredMax
        : Math.ceil(dataMax / 5) * 5;

    return max > min ? { max, min } : { max: min + 1, min };
}

function chartRatio(value: number, bounds: { max: number; min: number }): number {
    return Math.max(0, Math.min(1, (value - bounds.min) / (bounds.max - bounds.min)));
}

function chartGraphic(
    block: SiteBuilderBlock,
    rows: SiteBuilderChartRow[],
    series: SiteBuilderChartSeries[],
): ReactNode {
    const type = blockOptionText(block, "chartType", "barVertical");
    const bounds = chartBounds(block, rows);
    const borderWidth = Math.max(0, chartNumber(blockOption(block, "chartBorderWidth"), 2));
    const showTooltip = optionalBooleanValue(blockOption(block, "chartTooltip")) !== false;
    const tooltipText = (
        row: SiteBuilderChartRow,
        item: SiteBuilderChartSeries,
        seriesIndex: number,
    ) => `${row.label} · ${item.label}: ${row.values[seriesIndex] ?? 0}`;

    if (type === "pie" || type === "doughnut") {
        const values = rows.map((row) => Math.max(0, row.values[0] ?? 0));
        const total = Math.max(1, values.reduce((sum, value) => sum + value, 0));
        let offset = 0;
        const stops = values.map((value, index) => {
            const start = offset;
            offset += value / total * 360;
            const color = SITE_BUILDER_CHART_COLORS[index % SITE_BUILDER_CHART_COLORS.length] ?? "var(--site-primary)";

            return `${color} ${start}deg ${offset}deg`;
        });

        return (
            <div
                aria-hidden={showTooltip ? undefined : true}
                data-site-builder-chart-graphic={type}
                role={showTooltip ? "img" : undefined}
                style={{
                    alignSelf: "center",
                    aspectRatio: "1",
                    background: `conic-gradient(${stops.join(", ")})`,
                    borderRadius: "50%",
                    display: "grid",
                    maxWidth: "280px",
                    placeItems: "center",
                    width: "min(72%, 280px)",
                }}
                title={showTooltip
                    ? rows.map((row) => `${row.label}: ${row.values[0] ?? 0}`).join(", ")
                    : undefined}
            >
                {type === "doughnut" ? (
                    <span
                        style={{
                            background: "var(--site-surface, #fff)",
                            borderRadius: "50%",
                            height: "52%",
                            width: "52%",
                        }}
                    />
                ) : null}
            </div>
        );
    }

    const width = 640;
    const height = 280;
    const left = 54;
    const top = 18;
    const graphWidth = 560;
    const graphHeight = 220;

    if (type === "barHorizontal") {
        const rowHeight = graphHeight / Math.max(1, rows.length);
        const barHeight = Math.max(4, rowHeight * .68 / Math.max(1, series.length));

        return (
            <svg
                aria-hidden={showTooltip ? undefined : true}
                data-site-builder-chart-graphic={type}
                role="img"
                viewBox={`0 0 ${width} ${height}`}
            >
                {rows.map((row, rowIndex) => (
                    <g key={`${row.label}:${rowIndex}`}>
                        <text fill="currentColor" fontSize="11" x={left - 8} y={top + rowHeight * (rowIndex + .56)} textAnchor="end">
                            {row.label}
                        </text>
                        {series.map((item, seriesIndex) => {
                            const ratio = chartRatio(row.values[seriesIndex] ?? 0, bounds);

                            return (
                                <rect
                                    fill={item.color}
                                    height={barHeight}
                                    key={`${item.label}:${seriesIndex}`}
                                    rx={barHeight / 2}
                                    stroke={item.borderColor}
                                    strokeWidth={borderWidth}
                                    width={ratio * graphWidth}
                                    x={left}
                                    y={top + rowHeight * rowIndex + rowHeight * .16 + barHeight * seriesIndex}
                                >
                                    {showTooltip ? (
                                        <title>{tooltipText(row, item, seriesIndex)}</title>
                                    ) : null}
                                </rect>
                            );
                        })}
                    </g>
                ))}
            </svg>
        );
    }

    if (type === "line") {
        return (
            <svg
                aria-hidden={showTooltip ? undefined : true}
                data-site-builder-chart-graphic={type}
                role="img"
                viewBox={`0 0 ${width} ${height}`}
            >
                {[0, .25, .5, .75, 1].map((ratio) => (
                    <line
                        key={ratio}
                        stroke="currentColor"
                        strokeOpacity=".12"
                        x1={left}
                        x2={left + graphWidth}
                        y1={top + graphHeight * ratio}
                        y2={top + graphHeight * ratio}
                    />
                ))}
                {series.map((item, seriesIndex) => {
                    const points = rows.map((row, rowIndex) => {
                        const x = left + graphWidth * (rows.length === 1 ? .5 : rowIndex / (rows.length - 1));
                        const y = top + graphHeight * (1 - chartRatio(row.values[seriesIndex] ?? 0, bounds));

                        return { x, y };
                    });

                    return (
                        <g key={`${item.label}:${seriesIndex}`}>
                            <polyline
                                fill="none"
                                points={points.map((point) => `${point.x},${point.y}`).join(" ")}
                                stroke={item.borderColor}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={borderWidth}
                            />
                            {points.map((point, pointIndex) => (
                                <circle
                                    cx={point.x}
                                    cy={point.y}
                                    fill={item.color}
                                    key={pointIndex}
                                    r={Math.max(3, borderWidth + 1)}
                                >
                                    {showTooltip && rows[pointIndex] ? (
                                        <title>
                                            {tooltipText(rows[pointIndex], item, seriesIndex)}
                                        </title>
                                    ) : null}
                                </circle>
                            ))}
                        </g>
                    );
                })}
                {rows.map((row, index) => (
                    <text
                        fill="currentColor"
                        fontSize="11"
                        key={`${row.label}:${index}`}
                        textAnchor="middle"
                        x={left + graphWidth * (rows.length === 1 ? .5 : index / (rows.length - 1))}
                        y={height - 10}
                    >
                        {row.label}
                    </text>
                ))}
            </svg>
        );
    }

    if (type === "radar") {
        const centerX = width / 2;
        const centerY = height / 2;
        const radius = 106;
        const point = (index: number, ratio: number) => {
            const angle = (Math.PI * 2 * index / Math.max(3, rows.length)) - Math.PI / 2;

            return {
                x: centerX + Math.cos(angle) * radius * ratio,
                y: centerY + Math.sin(angle) * radius * ratio,
            };
        };

        return (
            <svg
                aria-hidden={showTooltip ? undefined : true}
                data-site-builder-chart-graphic={type}
                role="img"
                viewBox={`0 0 ${width} ${height}`}
            >
                {[.25, .5, .75, 1].map((ratio) => (
                    <polygon
                        fill="none"
                        key={ratio}
                        points={rows.map((_, index) => {
                            const coordinate = point(index, ratio);

                            return `${coordinate.x},${coordinate.y}`;
                        }).join(" ")}
                        stroke="currentColor"
                        strokeOpacity=".14"
                    />
                ))}
                {series.map((item, seriesIndex) => (
                    <polygon
                        fill={item.color}
                        fillOpacity=".2"
                        key={`${item.label}:${seriesIndex}`}
                        points={rows.map((row, index) => {
                            const coordinate = point(index, chartRatio(row.values[seriesIndex] ?? 0, bounds));

                            return `${coordinate.x},${coordinate.y}`;
                        }).join(" ")}
                        stroke={item.borderColor}
                        strokeWidth={borderWidth}
                    >
                        {showTooltip ? (
                            <title>
                                {rows.map((row) => (
                                    tooltipText(row, item, seriesIndex)
                                )).join(", ")}
                            </title>
                        ) : null}
                    </polygon>
                ))}
            </svg>
        );
    }

    const groupWidth = graphWidth / Math.max(1, rows.length);
    const barWidth = Math.max(4, groupWidth * .72 / Math.max(1, series.length));

    return (
        <svg
            aria-hidden={showTooltip ? undefined : true}
            data-site-builder-chart-graphic="barVertical"
            role="img"
            viewBox={`0 0 ${width} ${height}`}
        >
            {[0, .25, .5, .75, 1].map((ratio) => (
                <line
                    key={ratio}
                    stroke="currentColor"
                    strokeOpacity=".12"
                    x1={left}
                    x2={left + graphWidth}
                    y1={top + graphHeight * ratio}
                    y2={top + graphHeight * ratio}
                />
            ))}
            {rows.map((row, rowIndex) => (
                <g key={`${row.label}:${rowIndex}`}>
                    {series.map((item, seriesIndex) => {
                        const ratio = chartRatio(row.values[seriesIndex] ?? 0, bounds);
                        const renderedHeight = ratio * graphHeight;

                        return (
                            <rect
                                fill={item.color}
                                height={renderedHeight}
                                key={`${item.label}:${seriesIndex}`}
                                rx={Math.min(6, barWidth / 2)}
                                stroke={item.borderColor}
                                strokeWidth={borderWidth}
                                width={barWidth}
                                x={left + groupWidth * rowIndex + groupWidth * .14 + barWidth * seriesIndex}
                                y={top + graphHeight - renderedHeight}
                            >
                                {showTooltip ? (
                                    <title>{tooltipText(row, item, seriesIndex)}</title>
                                ) : null}
                            </rect>
                        );
                    })}
                    <text
                        fill="currentColor"
                        fontSize="11"
                        textAnchor="middle"
                        x={left + groupWidth * (rowIndex + .5)}
                        y={height - 10}
                    >
                        {row.label}
                    </text>
                </g>
            ))}
        </svg>
    );
}

function blocksInSlot(block: SiteBuilderBlock, key: string): SiteBuilderBlock[] {
    return Array.isArray(block.slots?.[key]) ? block.slots?.[key] ?? [] : [];
}

function allNestedBlocks(block: SiteBuilderBlock): NestedBlockEntry[] {
    const orderedSlotKeys = ["grid", "content", ...Object.keys(block.slots ?? {}).filter((key) => key !== "grid" && key !== "content")];
    const seen = new Set<string>();
    const slotBlocks = orderedSlotKeys.flatMap((key) => {
        if (seen.has(key)) {
            return [];
        }

        seen.add(key);

        return blocksInSlot(block, key).map((child, index) => ({
            block: child,
            index,
            slot: key,
        }));
    });

    return [
        ...slotBlocks,
        ...(block.children ?? []).map((child, index) => ({
            block: child,
            index,
            slot: "children",
        })),
    ];
}

function replaceRuntimeTokens(value: string, runtime: SiteBuilderRuntime): string {
    const replaced = value
        .replaceAll("{{site.name}}", runtime.site?.name ?? "")
        .replaceAll("{{page.title}}", runtime.page?.title ?? "")
        .replaceAll("{{clientPortal.url}}", runtime.site?.clientPortal?.url ?? "#")
        .replaceAll("{{currentYear}}", String(new Date().getFullYear()));

    return resolveSiteBuilderRuntimeHref(
        replaced,
        runtime.pages,
        runtime.page?.languageCode ?? runtime.site?.defaultLanguageCode,
    );
}

function authoredBlockHref(block: SiteBuilderBlock, href: string): string {
    if (
        booleanValue(blockOption(block, "requiresBookingPortal"))
        && href.trim() === "/rezervace"
    ) {
        return siteBuilderPageHrefToken("system:booking");
    }

    return href;
}

function cssLength(value: unknown): string | undefined {
    if (typeof value === "number") {
        return `${value}px`;
    }

    if (typeof value !== "string" || value.trim() === "") {
        return undefined;
    }

    return /^\d+(\.\d+)?$/.test(value.trim()) ? `${value.trim()}px` : value.trim();
}

function styleRecord(block: SiteBuilderBlock): StyleRecord {
    return siteBuilderBlockStyle(block);
}

function partResizeStyleRecord(
    block: SiteBuilderBlock,
    part: string,
): StyleRecord {
    const option = (key: string): unknown => {
        const partKey = siteBuilderPartOptionKey(part, key);

        return block.options?.[partKey];
    };
    const options = Object.fromEntries(
        SITE_BUILDER_PART_STYLE_KEYS.flatMap((key) => {
            const value = option(key);

            return value === undefined ? [] : [[key, value]];
        }),
    );

    return styleRecord({
        type: block.type,
        options,
    });
}

function horizontalAlignmentStyle(value: string): StyleRecord {
    return {
        justifyContent: value === "right" || value === "end"
            ? "flex-end"
            : value === "center"
                ? "center"
                : "flex-start",
    };
}

function buttonOptionStyle(block: SiteBuilderBlock): StyleRecord {
    const size = blockOptionText(block, "size");
    const shape = blockOptionText(block, "buttonShape");
    const sizeStyle: StyleRecord = size === "small"
        ? {
            fontSize: ".72rem",
            minHeight: "2.25rem",
            padding: ".65rem 1rem",
        }
        : size === "large"
            ? {
                fontSize: ".9rem",
                minHeight: "3.25rem",
                padding: "1.05rem 1.85rem",
            }
            : size === "medium"
                ? {
                    fontSize: ".8rem",
                    minHeight: "2.75rem",
                    padding: ".85rem 1.4rem",
                }
                : {};
    const shapeStyle: StyleRecord = shape === "pill"
        ? { borderRadius: "9999px" }
        : shape === "rounded"
            ? { borderRadius: "var(--site-radius, 16px)" }
            : shape === "square"
                ? { borderRadius: 0 }
                : {};

    return {
        alignItems: "center",
        display: "inline-flex",
        gap: ".55em",
        justifyContent: "center",
        maxWidth: "100%",
        minWidth: 0,
        overflowWrap: "anywhere",
        textDecoration: "none",
        whiteSpace: "normal",
        ...sizeStyle,
        ...shapeStyle,
    };
}

function alignedInlineItemStyle(block: SiteBuilderBlock): StyleRecord {
    const alignment = blockOptionText(block, "alignment");

    if (alignment === "center") {
        return { marginLeft: "auto", marginRight: "auto" };
    }

    if (alignment === "right") {
        return { marginLeft: "auto" };
    }

    if (alignment === "left") {
        return { marginRight: "auto" };
    }

    return {};
}

function badgeOptionStyle(block: SiteBuilderBlock): StyleRecord {
    const variant = blockOptionText(block, "badgeStyle");

    if (variant === "success") {
        return { backgroundColor: "#dcfce7", borderColor: "#86efac", color: "#166534" };
    }

    if (variant === "info") {
        return { backgroundColor: "#dbeafe", borderColor: "#93c5fd", color: "#1e40af" };
    }

    if (variant === "warning") {
        return { backgroundColor: "#fef3c7", borderColor: "#fcd34d", color: "#92400e" };
    }

    if (variant === "danger") {
        return { backgroundColor: "#fee2e2", borderColor: "#fca5a5", color: "#991b1b" };
    }

    if (variant === "dark") {
        return { backgroundColor: "var(--site-inverse)", color: "var(--site-on-inverse)" };
    }

    if (variant === "light") {
        return { backgroundColor: "var(--site-surface)", color: "var(--site-on-surface)" };
    }

    if (variant === "primary") {
        return { backgroundColor: "var(--site-primary)", color: "var(--site-on-primary)" };
    }

    if (variant === "secondary") {
        return { backgroundColor: "var(--site-accent)", color: "var(--site-on-accent)" };
    }

    return {};
}

function gridItemStyle(block: SiteBuilderBlock): StyleRecord {
    const gridX = Math.max(1, numberValue(blockOption(block, "gridX"), 1));
    const gridY = Math.max(1, numberValue(blockOption(block, "gridY"), 1));
    const gridW = Math.max(1, numberValue(blockOption(block, "gridW"), 12));
    const gridH = Math.max(1, numberValue(blockOption(block, "gridH"), 1));
    const hasGridColumnPlacement = ["gridX", "gridW"].some(
        (key) => hasPositiveGridNumberOption(block, key),
    );
    const hasGridRowPlacement = ["gridY", "gridH"].some(
        (key) => hasPositiveGridNumberOption(block, key),
    );

    return hasGridColumnPlacement || hasGridRowPlacement ? {
        gridColumn: hasGridColumnPlacement ? `${gridX} / span ${gridW}` : undefined,
        gridRow: hasGridRowPlacement ? `${gridY} / span ${gridH}` : undefined,
        minWidth: 0,
    } : {};
}

function gridRowCount(blocks: SiteBuilderBlock[]): number {
    return Math.max(
        1,
        ...blocks.map((block) => {
            const row = Math.max(1, numberValue(blockOption(block, "gridY"), 1));
            const height = Math.max(1, numberValue(blockOption(block, "gridH"), 1));

            return row + height - 1;
        }),
    );
}

function gridDocumentOrder(style: StyleRecord): number | undefined {
    const row = String(style.gridRow ?? "").match(/^(\d+)/u)?.[1];
    const column = String(style.gridColumn ?? "").match(/^(\d+)/u)?.[1];

    return row
        ? (Number(row) * 100) + Number(column ?? 1)
        : undefined;
}

function authoredGridItemStyle(block: SiteBuilderBlock, style: StyleRecord): StyleRecord {
    const column = style.gridColumn;
    const row = style.gridRow;
    const hasAuthoredColumn = ["gridX", "gridW"].some(
        (key) => hasPositiveGridNumberOption(block, key),
    );
    const hasAuthoredRow = ["gridY", "gridH"].some(
        (key) => hasPositiveGridNumberOption(block, key),
    );

    return {
        ...style,
        ...(!hasAuthoredColumn || column === undefined
            ? {}
            : { "--site-builder-authored-grid-column": String(column) }),
        ...(!hasAuthoredRow || row === undefined
            ? {}
            : { "--site-builder-authored-grid-row": String(row) }),
    };
}

function inlineTextIsEmpty(block: SiteBuilderBlock, key: string): boolean {
    const segments = block.richText?.[key];

    if (segments && segments.length > 0) {
        return segments.every((segment) => segment.text.trim() === "");
    }

    return blockText(block, key).trim() === "";
}

function cardStyle(block: SiteBuilderBlock): StyleRecord {
    const configured = styleRecord(block);
    const variant = blockOptionText(block, "cardVariant", "default");
    const variantStyle: StyleRecord = variant === "outlined"
        ? {
            backgroundColor: "transparent",
            boxShadow: "none",
        }
        : variant === "elevated"
            ? {
                borderColor: "transparent",
                boxShadow: "0 22px 54px color-mix(in srgb, var(--site-shadow-color, var(--site-text)) 16%, transparent)",
            }
            : variant === "glass"
                ? {
                    backdropFilter: "blur(18px)",
                    backgroundColor: "color-mix(in srgb, var(--site-surface) 72%, transparent)",
                    backgroundImage:
                        "linear-gradient(135deg, color-mix(in srgb, var(--site-on-surface, var(--site-text)) 12%, transparent), transparent)",
                    borderColor: "color-mix(in srgb, var(--site-border) 72%, transparent)",
                    boxShadow: "0 18px 48px color-mix(in srgb, var(--site-shadow-color, var(--site-text)) 12%, transparent)",
                }
                : {
                    boxShadow: "0 16px 42px color-mix(in srgb, var(--site-shadow-color, var(--site-text)) 8%, transparent)",
                };

    return {
        display: "grid",
        minWidth: 0,
        alignContent: configured.alignContent ?? "start",
        gap: configured.gap ?? "1rem",
        overflow: configured.overflow ?? "hidden",
        backgroundColor: configured.backgroundColor ?? "var(--site-surface, #ffffff)",
        borderColor: configured.borderColor ?? "var(--site-border, rgba(15, 23, 42, .12))",
        borderStyle: configured.borderStyle ?? "solid",
        borderWidth: configured.borderWidth ?? "1px",
        borderRadius: configured.borderRadius ?? "var(--site-radius, 16px)",
        padding: configured.padding ?? "clamp(1rem, 2vw, 1.5rem)",
        ...variantStyle,
        ...configured,
    };
}

function richText(block: SiteBuilderBlock, key: string, runtime: SiteBuilderRuntime, fallback = ""): ReactNode {
    const segments = block.richText?.[key];

    if (!segments || segments.length === 0) {
        const runtimeText = blockOptionText(block, "runtimeText");

        if (runtimeText === "siteName") {
            return runtime.site?.name ?? blockText(block, key, fallback);
        }

        if (runtimeText === "pageTitle") {
            return runtime.page?.title ?? blockText(block, key, fallback);
        }

        if (runtimeText === "copyright") {
            return `© ${new Date().getFullYear()} ${runtime.site?.name ?? blockText(block, key, fallback)}`;
        }

        const value = replaceRuntimeTokens(blockText(block, key, fallback), runtime);

        return value === "" && runtime.editorMode ? "\u200b" : value;
    }

    const empty = segments.every((segment) => segment.text === "");

    return segments.map((segment, index) => (
        <span
            key={`${block.uuid ?? block.type}:${key}:${index}`}
            style={{
                color: segment.marks?.textColor,
                fontFamily: segment.marks?.fontFamily,
                fontSize: segment.marks?.fontSize,
                fontStyle: segment.marks?.fontStyle,
                fontWeight: segment.marks?.fontWeight,
                lineHeight: segment.marks?.lineHeight,
                textAlign: segment.marks?.textAlign as CSSProperties["textAlign"],
                textDecoration: segment.marks?.textDecoration,
            }}
        >
            {empty && runtime.editorMode && index === 0
                ? "\u200b"
                : replaceRuntimeTokens(segment.text, runtime)}
        </span>
    ));
}

function link(
    href: string,
    className: string,
    children: ReactNode,
    runtime: SiteBuilderRuntime,
    style?: CSSProperties,
    openInNewWindow?: boolean,
    outlineOwner = false,
): ReactNode {
    const resolvedHref = replaceRuntimeTokens(href || "#", runtime);
    const external = /^https?:\/\//i.test(resolvedHref);
    const opensNewWindow = openInNewWindow ?? external;
    const internal = !resolvedHref.startsWith("//")
        && !/^[a-z][a-z\d+.-]*:/i.test(resolvedHref)
        && !resolvedHref.startsWith("\\");
    const outlineOwnerAttributes = siteBuilderEditorOutlineOwnerAttributes(
        runtime.editorMode && outlineOwner,
    );

    if (
        runtime.editorMode
        || runtime.readOnly
        || (internal && !opensNewWindow)
    ) {
        return (
            <runtime.LinkComponent
                {...outlineOwnerAttributes}
                className={className}
                href={resolvedHref}
                rel={opensNewWindow ? "noopener noreferrer" : undefined}
                style={style}
                target={opensNewWindow ? "_blank" : undefined}
            >
                {children}
            </runtime.LinkComponent>
        );
    }

    return (
        <a
            {...outlineOwnerAttributes}
            href={resolvedHref}
            className={className}
            target={opensNewWindow ? "_blank" : undefined}
            rel={opensNewWindow ? "noopener noreferrer" : undefined}
            style={style}
        >
            {children}
        </a>
    );
}

function runtimeRole(block: SiteBuilderBlock): string {
    return blockOptionText(block, "runtimeRole")
        || blockOptionText(block, "siteRuntimeRole")
        || blockOptionText(block, "role");
}

function region(block: SiteBuilderBlock): string {
    return blockOptionText(block, "webRegion")
        || blockOptionText(block, "siteBuilderRegion")
        || blockOptionText(block, "restaurantRegion")
        || blockOptionText(block, "region");
}

function isRuntimeNavigationBlock(block: SiteBuilderBlock): boolean {
    if (!isSiteNavigationBlock(block)) {
        return false;
    }

    const documentRole = blockOptionText(block, "documentRole");

    return block.type === "navigation"
        || block.type === "headerMenu"
        || runtimeRole(block) === "navigation"
        || documentRole === "headerNavigation"
        || documentRole === "pageNavigation"
        || documentRole === "headerMenu";
}

function navigationLink(
    item: Parameters<NonNullable<Parameters<typeof SiteNavigation>[0]["renderLink"]>>[0]["item"],
    href: string,
    className: string,
    children: ReactNode,
    runtime: SiteBuilderRuntime,
    onClick?: () => void,
    id?: string,
    ariaCurrent?: "page",
): ReactNode {
    const resolvedHref = replaceRuntimeTokens(href, runtime);

    return (
        <runtime.LinkComponent
            aria-current={ariaCurrent}
            className={className}
            data-navigation-item-type={item.type}
            data-site-navigation-item-uuid={item.uuid}
            data-site-navigation-link-owner="true"
            href={resolvedHref}
            id={id}
            onClick={onClick}
            rel={item.openInNewTab ? "noopener noreferrer" : undefined}
            target={item.openInNewTab ? "_blank" : undefined}
        >
            {children}
        </runtime.LinkComponent>
    );
}

function navigation(block: SiteBuilderBlock, runtime: SiteBuilderRuntime): ReactNode {
    const templateCode = runtime.site?.rendererCode ?? runtime.site?.templateCode;
    const usesContainerBreakpoint = webSiteNavigationUsesContainerBreakpoint(templateCode);
    const languageCode = runtime.page?.languageCode
        ?? runtime.site?.defaultLanguageCode
        ?? null;
    const commonInput = {
        block,
        clientPortal: runtime.site?.clientPortal,
        currentPageUuid: runtime.page?.uuid,
        design: runtime.design,
        languageCode,
        menus: runtime.menus,
        pages: runtime.pages,
        templateCode,
    };
    const desktop = resolveWebSiteNavigationModel({
        ...commonInput,
        viewport: "desktop",
    });
    const mobile = resolveWebSiteNavigationModel({
        ...commonInput,
        viewport: "mobile",
    });
    const renderLink: NonNullable<Parameters<typeof SiteNavigation>[0]["renderLink"]> = ({
        ariaCurrent,
        children,
        className,
        href,
        id,
        item,
        onClick,
    }) => navigationLink(
        item,
        href,
        className,
        children,
        runtime,
        onClick,
        id,
        ariaCurrent,
    );
    const runtimeRenderLink = runtime.editorMode
        || runtime.readOnly === true
        || runtime.navigationReadOnly
        ? renderLink
        : undefined;

    return (
        <>
            <style data-web-site-navigation-responsive="true">{`
                .web-site-navigation-mobile { display: none; }
                @media (max-width: 900px) {
                    .web-site-navigation-desktop { display: none !important; }
                    .web-site-navigation-mobile { display: block; width: 100%; }
                }
                @container (max-width: 900px) {
                    .web-site-navigation-container-responsive.web-site-navigation-desktop { display: none !important; }
                    .web-site-navigation-container-responsive.web-site-navigation-mobile { display: block; width: 100%; }
                }
            `}</style>
            <SiteNavigationStyles />
            <div className={`web-site-navigation-desktop${usesContainerBreakpoint
                ? " web-site-navigation-container-responsive"
                : ""}`}>
                <SiteNavigation
                    activeItemUuid={desktop.activeItemUuid ?? undefined}
                    className="site-builder-navigation web-site-navigation"
                    includeStyles={false}
                    items={desktop.items}
                    labels={desktop.labels}
                    mode="desktop"
                    preservePanelOnLinkClick={runtime.editorMode}
                    readOnly={runtime.navigationReadOnly}
                    renderLink={runtimeRenderLink}
                    settings={desktop.settings}
                    style={desktop.style as CSSProperties}
                />
            </div>
            <div className={`web-site-navigation-mobile${usesContainerBreakpoint
                ? " web-site-navigation-container-responsive"
                : ""}`}>
                <SiteNavigation
                    activeItemUuid={mobile.activeItemUuid ?? undefined}
                    className="site-builder-navigation web-site-navigation"
                    includeStyles={false}
                    items={mobile.items}
                    labels={mobile.labels}
                    mode="mobile"
                    preservePanelOnLinkClick={runtime.editorMode}
                    readOnly={runtime.navigationReadOnly}
                    renderLink={runtimeRenderLink}
                    settings={mobile.settings}
                    style={mobile.style as CSSProperties}
                />
            </div>
        </>
    );
}

function nestedBlocks(
    block: SiteBuilderBlock,
    runtime: SiteBuilderRuntime,
    parentPath: string,
): ReactNode {
    const entries = allNestedBlocks(block);
    const renderEntry = (entry: NestedBlockEntry): ReactNode => (
        <SiteBuilderBlockView
            key={entry.block.uuid ?? `${entry.block.type}:${entry.index}`}
            block={entry.block}
            lockedColumnParentPart={
                block.type === "column"
                && siteBuilderBlockDelegatesOutlineToColumn(entry.block)
                    ? parentPath
                    : undefined
            }
            parentType={block.type}
            path={siteBuilderEditorChildPath(
                parentPath,
                entry.slot,
                entry.block,
                entry.index,
            )}
            runtime={runtime}
            slot={entry.slot}
        />
    );
    const renderedEntries: ReactNode[] = [];
    let entryIndex = 0;

    while (entryIndex < entries.length) {
        const entry = entries[entryIndex];
        const groupId = entry.block.type === "column"
            ? blockOptionText(entry.block, "columnGroup")
            : "";

        if (!groupId) {
            renderedEntries.push(renderEntry(entry));
            entryIndex += 1;
            continue;
        }

        const groupEntries: NestedBlockEntry[] = [];

        while (entryIndex < entries.length) {
            const candidate = entries[entryIndex];
            const candidateGroupId = candidate.block.type === "column"
                ? blockOptionText(candidate.block, "columnGroup")
                : "";

            if (
                candidate.slot !== entry.slot
                || candidateGroupId !== groupId
            ) {
                break;
            }

            groupEntries.push(candidate);
            entryIndex += 1;
        }

        if (groupEntries.length < 2) {
            renderedEntries.push(renderEntry(groupEntries[0]));
            continue;
        }

        renderedEntries.push(
            <div
                className="site-builder-column-group"
                data-builder-column-group={groupId}
                key={`${entry.slot}:column-group:${groupId}`}
                style={{
                    display: "grid",
                    gap: "1rem",
                    gridTemplateColumns: `repeat(${groupEntries.length}, minmax(0, 1fr))`,
                    minWidth: 0,
                }}
            >
                {groupEntries.map(renderEntry)}
            </div>,
        );
    }

    if (isRuntimeNavigationBlock(block)) {
        return navigation(block, runtime);
    }

    if (runtimeRole(block) === "headerActions") {
        return runtime.headerActions ?? null;
    }

    return <>{renderedEntries}</>;
}

function textElement(block: SiteBuilderBlock, runtime: SiteBuilderRuntime): ReactNode {
    const key = ["text", "title", "label", "value"].find((candidate) => block.content?.[candidate] !== undefined) ?? "text";
    const variant = blockOptionText(block, "textRole") || blockOptionText(block, "variant") || blockOptionText(block, "tag");
    const content = richText(block, key, runtime);
    const className = `site-builder-text site-builder-text-${variant || "body"}`;
    const roleStyle: StyleRecord = variant === "lead"
        ? {
            fontSize: "clamp(1.125rem, 2vw, 1.35rem)",
            lineHeight: 1.65,
        }
        : variant === "eyebrow"
            ? {
                fontSize: ".75rem",
                fontWeight: 800,
                letterSpacing: ".12em",
                textTransform: "uppercase",
            }
            : variant === "h1"
                ? {
                    fontSize: "clamp(2.5rem, 12cqw, 5.5rem)",
                    fontWeight: 800,
                    lineHeight: 1.02,
                }
                : variant === "h2"
                    ? {
                        fontSize: "clamp(2rem, 9cqw, 3.75rem)",
                        fontWeight: 750,
                        lineHeight: 1.08,
                    }
                    : variant === "h3"
                        ? {
                            fontSize: "clamp(1.35rem, 6cqw, 2rem)",
                            fontWeight: 700,
                            lineHeight: 1.2,
                        }
                        : {};
    const style: CSSProperties = {
        margin: 0,
        maxWidth: "100%",
        minWidth: 0,
        overflowWrap: "anywhere",
        whiteSpace: "pre-line",
        ...roleStyle,
        ...styleRecord(block),
        ...partResizeStyleRecord(block, key),
    };
    const textAttributes = siteBuilderEditorPartAttributes({
        editorMode: runtime.editorMode,
        inlineText: true,
        part: key,
    });
    const placeholderAttributes = siteBuilderEditorInlinePlaceholderAttributes({
        block,
        editorMode: runtime.editorMode,
        empty: inlineTextIsEmpty(block, key),
    });
    const outlineOwnerAttributes = siteBuilderEditorOutlineOwnerAttributes(
        runtime.editorMode,
    );

    if (variant === "h1" || variant === "heroTitle") {
        return <h1 {...textAttributes} {...placeholderAttributes} {...outlineOwnerAttributes} className={className} style={style}>{content}</h1>;
    }

    if (variant === "h2" || variant === "sectionTitle") {
        return <h2 {...textAttributes} {...placeholderAttributes} {...outlineOwnerAttributes} className={className} style={style}>{content}</h2>;
    }

    if (variant === "h3" || variant === "cardTitle") {
        return <h3 {...textAttributes} {...placeholderAttributes} {...outlineOwnerAttributes} className={className} style={style}>{content}</h3>;
    }

    const href = blockText(block, "href");

    if (href) {
        return link(
            authoredBlockHref(block, href),
            className,
            <span {...textAttributes} {...placeholderAttributes} {...outlineOwnerAttributes} style={style}>{content}</span>,
            runtime,
        );
    }

    return <p {...textAttributes} {...placeholderAttributes} {...outlineOwnerAttributes} className={className} style={style}>{content}</p>;
}

function media(block: SiteBuilderBlock, runtime: SiteBuilderRuntime): ReactNode {
    const presentation = siteBuilderMediaPresentation(block.content);
    const aspectRatio = siteBuilderAspectRatio(
        blockOptionText(block, "aspectRatio"),
    );
    const configuredStyle = styleRecord(block);
    const imageResizeStyle = partResizeStyleRecord(block, "image");
    const shape = blockOptionText(block, "shape");
    const stretch = optionalBooleanValue(blockOption(block, "stretch"));
    const shapeStyle: CSSProperties = shape === "circle"
        ? { borderRadius: "50%" }
        : shape === "arch"
            ? { borderRadius: "999px 999px 0 0" }
            : shape === "rounded"
                ? { borderRadius: "var(--site-radius, 16px)" }
                : shape === "none"
                    ? { borderRadius: 0 }
                    : {};

    if (presentation.kind === "empty") {
        return runtime.editorMode ? (
            <div
                {...siteBuilderEditorPartAttributes({
                    editorMode: true,
                    part: "media",
                })}
                {...siteBuilderEditorOutlineOwnerAttributes(true)}
                className="site-builder-media-placeholder"
                data-site-builder-media-placeholder="true"
                style={{
                    alignItems: "center",
                    aspectRatio: aspectRatio ?? "4 / 3",
                    border: "1px dashed var(--site-border)",
                    borderRadius: "var(--site-radius, 16px)",
                    display: "grid",
                    minHeight: "8rem",
                    placeItems: "center",
                    width: "100%",
                    ...configuredStyle,
                    ...partResizeStyleRecord(block, "media"),
                }}
            >
                {blockText(block, "title", block.type === "image" ? "Image" : "Media")}
            </div>
        ) : null;
    }

    if (presentation.kind === "video") {
        const videoSource = siteVideoSource(presentation.source);
        const videoStyle = {
            ...configuredStyle,
            aspectRatio: aspectRatio ?? "16 / 9",
            height: "auto",
            minHeight: 0,
            pointerEvents: runtime.editorMode ? "none" : undefined,
            width: "100%",
            ...partResizeStyleRecord(block, "media"),
        } satisfies CSSProperties;

        return videoSource?.kind === "embed" ? (
            <iframe
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    part: "media",
                })}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                allowFullScreen
                className="site-builder-media-video"
                src={videoSource.source}
                style={videoStyle}
                tabIndex={runtime.editorMode ? -1 : undefined}
                title={blockText(block, "title", "Media")}
            />
        ) : videoSource?.kind === "file" ? (
            <video
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    part: "media",
                })}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                className="site-builder-media-video"
                controls
                playsInline
                src={videoSource.source}
                style={videoStyle}
                tabIndex={runtime.editorMode ? -1 : undefined}
                title={blockText(block, "title", "Media")}
            />
        ) : runtime.editorMode ? (
            <div
                className="site-builder-media-placeholder"
                data-site-builder-media-placeholder="invalid-video"
                style={{
                    ...configuredStyle,
                    ...partResizeStyleRecord(block, "media"),
                }}
            >
                {blockText(block, "title", "Media")}
            </div>
        ) : null;
    }

    return (
        <img
            {...siteBuilderEditorPartAttributes({
                editorMode: runtime.editorMode,
                part: "image",
            })}
            {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
            src={presentation.source}
            alt={blockText(block, "alt")}
            title={blockText(block, "imageTitle") || undefined}
            className="site-builder-image"
            loading={booleanValue(blockOption(block, "eager")) ? "eager" : "lazy"}
            style={{
                display: "block",
                maxWidth: "100%",
                minWidth: 0,
                ...shapeStyle,
                ...configuredStyle,
                aspectRatio,
                objectFit: blockFieldText(
                    block,
                    "options",
                    "objectFit",
                    "cover",
                ) as CSSProperties["objectFit"],
                objectPosition: blockOptionText(block, "objectPosition", "center"),
                ...(stretch === true
                    ? { width: "100%" }
                    : stretch === false
                        ? { maxWidth: "100%", width: configuredStyle.width ?? "auto" }
                        : {}),
                ...imageResizeStyle,
            }}
        />
    );
}

function form(
    block: SiteBuilderBlock,
    runtime: SiteBuilderRuntime,
    path: string,
): ReactNode {
    const config = siteBuilderFormConfig(block);
    const itemBlocks = blocksInSlot(block, "items");
    const columns = Math.max(1, Math.min(4, Math.trunc(numberValue(blockOption(block, "columns"), 1))));
    const renderAuthoredItems = (
        items: readonly SiteBuilderBlock[],
    ): ReactNode => (
        <div
            {...siteBuilderEditorSlotAttributes({
                editorMode: runtime.editorMode,
                itemCount: items.length,
                slot: "items",
            })}
            className="site-builder-form-fields"
            data-builder-empty-slot={
                runtime.editorMode && items.length === 0 ? "true" : undefined
            }
            data-builder-form-drop-slot={runtime.editorMode ? "true" : undefined}
            role="group"
            style={{
                "--site-form-columns": String(columns),
                background: runtime.editorMode && items.length === 0
                    ? "color-mix(in srgb, var(--site-accent) 4%, transparent)"
                    : undefined,
                boxSizing: "border-box",
                display: "grid",
                gap: cssLength(blockOption(block, "gap")) ?? "1rem",
                gridTemplateColumns: "repeat(var(--site-form-columns, 1), minmax(0, 1fr))",
                minHeight: runtime.editorMode && items.length === 0 ? "96px" : undefined,
                outline: runtime.editorMode && items.length === 0
                    ? "1px dashed color-mix(in srgb, var(--site-accent) 48%, transparent)"
                    : undefined,
                outlineOffset: runtime.editorMode && items.length === 0 ? "-2px" : undefined,
                padding: runtime.editorMode && items.length === 0 ? "12px" : undefined,
            } as CSSProperties}
        >
            {items.map((child, index) => (
                <SiteBuilderBlockView
                    key={`${child.uuid ?? child.type}:${index}`}
                    block={child}
                    parentType={block.type}
                    path={siteBuilderEditorChildPath(path, "items", child, index)}
                    runtime={runtime}
                    slot="items"
                />
            ))}
        </div>
    );
    const renderedForm = runtime.renderForm?.({
        buttonBackgroundColor: blockOptionText(block, "buttonBackgroundColor") || null,
        buttonTextColor: blockOptionText(block, "buttonTextColor") || null,
        columns,
        design: runtime.design,
        fieldGap: cssLength(blockOption(block, "gap")) ?? null,
        formBlock: block,
        formKey: config.formKey,
        hasAuthoredItems: itemBlocks.length > 0,
        readOnly: runtime.editorMode || (runtime.readOnly ?? false),
        renderAuthoredItems,
        submitLabel: config.submitLabel,
        successMessage: config.successMessage,
    }) ?? null;
    const authoredEditorPreview = siteBuilderUsesAuthoredFormPreview(
        runtime.editorMode,
        itemBlocks.length,
    );
    const editorRuntimePreview = runtime.editorMode && !authoredEditorPreview;

    return (
        <div
            {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
            className="site-builder-form"
            style={{ containerType: "inline-size", ...styleRecord(block) }}
        >
            {blockText(block, "title") ? (
                <h2
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "title",
                    })}
                    className="site-builder-form-title"
                    style={partResizeStyleRecord(block, "title")}
                >
                    {richText(block, "title", runtime)}
                </h2>
            ) : null}
            {blockText(block, "text") ? (
                <p
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "text",
                    })}
                    className="site-builder-form-text"
                    style={partResizeStyleRecord(block, "text")}
                >
                    {richText(block, "text", runtime)}
                </p>
            ) : null}
            {authoredEditorPreview ? renderAuthoredItems(itemBlocks) : editorRuntimePreview ? (
                <div
                    {...siteBuilderEditorSlotAttributes({
                        editorMode: true,
                        itemCount: 0,
                        slot: "items",
                    })}
                    className="site-builder-form-runtime-preview"
                    data-builder-empty-slot={renderedForm === null ? "true" : undefined}
                    data-builder-form-drop-slot="true"
                    role="group"
                    style={{
                        background: renderedForm === null
                            ? "color-mix(in srgb, var(--site-accent) 4%, transparent)"
                            : undefined,
                        boxSizing: "border-box",
                        minHeight: renderedForm === null ? "96px" : undefined,
                        outline: renderedForm === null
                            ? "1px dashed color-mix(in srgb, var(--site-accent) 48%, transparent)"
                            : undefined,
                        outlineOffset: renderedForm === null ? "-2px" : undefined,
                        padding: renderedForm === null ? "12px" : undefined,
                        width: "100%",
                    }}
                >
                    {renderedForm}
                </div>
            ) : renderedForm}
            {authoredEditorPreview && config.submitLabel ? (
                <div
                    className="site-builder-form-submit-preview"
                    style={{ display: "flex", marginTop: cssLength(blockOption(block, "gap")) ?? "1rem" }}
                >
                    <button
                        className={`site-builder-button ${siteBuilderButtonClass(runtime.design.components.buttons)}`}
                        disabled
                        style={{
                            backgroundColor: blockOptionText(block, "buttonBackgroundColor") || undefined,
                            color: blockOptionText(block, "buttonTextColor") || undefined,
                        }}
                        type="button"
                    >
                        <span
                            {...siteBuilderEditorPartAttributes({
                                editorMode: runtime.editorMode,
                                inlineText: true,
                                part: "submitLabel",
                            })}
                            style={partResizeStyleRecord(block, "submitLabel")}
                        >
                            {richText(
                                block,
                                "submitLabel",
                                runtime,
                                config.submitLabel,
                            )}
                        </span>
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function mapSourceWithZoom(source: string, value: unknown): string {
    const numeric = typeof value === "number" ? value : Number(value);

    if (
        (typeof value !== "number" && typeof value !== "string")
        || String(value).trim() === ""
        || !Number.isFinite(numeric)
    ) {
        return source;
    }

    const zoom = Math.max(1, Math.min(22, Math.round(numeric)));

    try {
        const url = new URL(source);
        const hostname = url.hostname.toLowerCase();
        const googleMaps = hostname === "google.com"
            || hostname.endsWith(".google.com")
            || hostname === "maps.google.com"
            || /^(?:[^.]+\.)*google\.[a-z]{2,3}(?:\.[a-z]{2})?$/.test(hostname);

        if (!googleMaps) {
            return source;
        }

        url.searchParams.set("z", String(zoom));

        return url.toString();
    } catch {
        return source;
    }
}

function mapStyleFilter(value: string): CSSProperties["filter"] | undefined {
    if (value === "dark") {
        return "grayscale(1) invert(.92) contrast(.88) saturate(.35)";
    }

    if (value === "grayscale") {
        return "grayscale(1)";
    }

    return undefined;
}

function map(block: SiteBuilderBlock, runtime: SiteBuilderRuntime): ReactNode {
    const address = firstBlockText(block, ["address", "text"]);
    const explicitUrl = firstBlockText(block, ["mapUrl", "url", "embedUrl"]);
    const mapStyle = blockOptionText(block, "mapStyle", "default");
    const normalizedSource = explicitUrl
        ? siteEmbedSource(explicitUrl)
        : siteMapEmbedSource({
            address,
            latitude: block.content?.latitude as number | string | null | undefined,
            longitude: block.content?.longitude as number | string | null | undefined,
        });
    const source = normalizedSource
        ? mapSourceWithZoom(normalizedSource, blockOption(block, "mapZoom"))
        : null;
    const configuredStyle = styleRecord(block);
    const mapResizeStyle = partResizeStyleRecord(block, "map");
    const mapAspectRatio = siteBuilderAspectRatio(
        blockOptionText(block, "mapAspectRatio", "wide"),
    );

    if (!source) {
        return address ? (
            <address
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: "address",
                })}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                className="site-builder-address"
                style={{
                    ...configuredStyle,
                    ...partResizeStyleRecord(block, "address"),
                }}
            >
                {address}
            </address>
        ) : null;
    }

    return (
        <iframe
            {...siteBuilderEditorPartAttributes({
                editorMode: runtime.editorMode,
                part: "map",
            })}
            {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
            className="site-builder-map"
            data-site-builder-map-style={mapStyle}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-popups"
            src={source}
            style={{
                display: "block",
                ...configuredStyle,
                width: configuredStyle.width ?? "100%",
                aspectRatio: mapAspectRatio,
                ...(mapAspectRatio
                    ? {
                        height: configuredStyle.height ?? "auto",
                        minHeight: configuredStyle.minHeight ?? 0,
                        width: configuredStyle.width ?? "100%",
                    }
                    : {}),
                filter: mapStyleFilter(mapStyle),
                pointerEvents: runtime.editorMode ? "none" : undefined,
                ...mapResizeStyle,
            }}
            tabIndex={runtime.editorMode ? -1 : undefined}
            title={blockText(block, "title", address || "Map")}
        />
    );
}

function socialNetwork(block: SiteBuilderBlock): string {
    const icon = blockText(block, "icon");

    if (icon.startsWith("social:")) {
        return icon.slice("social:".length).toLowerCase();
    }

    const href = firstBlockText(block, ["href", "url"]).toLowerCase();

    if (href.startsWith("mailto:")) return "email";
    if (href.includes("facebook.") || href.includes("fb.com")) return "facebook";
    if (href.includes("instagram.")) return "instagram";
    if (href.includes("youtube.") || href.includes("youtu.be")) return "youtube";
    if (href.includes("linkedin.")) return "linkedin";
    if (href.includes("tiktok.")) return "tiktok";
    if (href.includes("pinterest.")) return "pinterest";
    if (href.includes("whatsapp.") || href.includes("wa.me")) return "whatsapp";
    if (href.includes("twitter.") || href.includes("x.com")) return "x";

    return "link";
}

function socialNetworkColor(network: string): string {
    if (network === "facebook") return "#1877f2";
    if (network === "instagram") return "#e1306c";
    if (network === "youtube") return "#ff0000";
    if (network === "linkedin") return "#0a66c2";
    if (network === "tiktok") return "#111111";
    if (network === "pinterest") return "#e60023";
    if (network === "whatsapp") return "#25d366";
    if (network === "x") return "var(--site-text)";

    return "currentColor";
}

function socialLinkOptionStyle(block: SiteBuilderBlock): StyleRecord {
    const share = block.type === "share"
        || blockOption(block, "shareIconLayout") !== undefined
        || blockOption(block, "shareIconSize") !== undefined;
    const layout = blockOptionText(
        block,
        share ? "shareIconLayout" : "socialIconLayout",
        "disk",
    );
    const rawSize = numberValue(
        blockOption(
            block,
            share ? "shareIconSize" : "socialIconSize",
        ),
        40,
    );
    const size = `${Math.max(12, Math.min(160, Math.round(rawSize)))}px`;
    const theme = blockOptionText(block, "socialIconTheme", "default");
    const colored = share
        ? optionalBooleanValue(blockOption(block, "shareColored")) !== false
        : optionalBooleanValue(blockOption(block, "socialIconBrandColor")) !== false;
    const network = socialNetwork(block);
    const customColor = blockOptionText(block, "socialIconColor")
        || blockOptionText(block, "iconColor");
    const themeColor = theme === "dark"
        ? "var(--site-on-inverse)"
        : theme === "light"
            ? "var(--site-on-surface)"
            : "currentColor";
    const backgroundColor = layout === "none"
        ? "transparent"
        : blockOptionText(block, "socialIconBackgroundColor")
            || (theme === "dark"
                ? "var(--site-inverse)"
                : "var(--site-surface)");
    const linkStyle = blockOptionText(
        block,
        share ? "shareLinkStyle" : "socialLinkStyle",
        "noUnderline",
    );

    return {
        alignItems: "center",
        backgroundColor,
        borderBottomColor: linkStyle === "underlineAlways"
            ? "currentColor"
            : "transparent",
        borderBottomStyle: "solid",
        borderBottomWidth: linkStyle === "noUnderline" ? 0 : "2px",
        borderRadius: layout === "square"
            ? 0
            : layout === "disk"
                ? "12px"
                : "9999px",
        color: customColor || (colored ? socialNetworkColor(network) : themeColor),
        display: "inline-grid",
        height: size,
        justifyContent: "center",
        minHeight: size,
        padding: 0,
        textDecoration: linkStyle === "underlineAlways"
            ? "underline"
            : "none",
        width: size,
    };
}

function socialLinks(
    block: SiteBuilderBlock,
    runtime: SiteBuilderRuntime,
    path: string,
): ReactNode {
    const networks = ["facebook", "instagram", "linkedin", "youtube", "tiktok", "x"];
    const slotItems = blocksInSlot(block, "items");
    const configuredItems = Array.isArray(block.content?.items)
        ? block.content.items.flatMap((item) => {
            const candidate = record(item);
            const href = textValue(candidate.href);
            const label = textValue(candidate.label, textValue(candidate.network));

            return href ? [{ href, label }] : [];
        })
        : [];
    const legacyItems = configuredItems.length > 0
        ? configuredItems
        : networks.flatMap((network) => {
            const href = blockText(block, network);

            return href ? [{ href, label: network }] : [];
        });

    const share = block.type === "share";
    const alignment = blockOptionText(
        block,
        share ? "shareAlignment" : "socialAlignment",
        "left",
    );
    const titlePosition = blockOptionText(
        block,
        share ? "shareTitlePosition" : "socialTitlePosition",
        "none",
    );
    const title = blockText(block, "title");
    const titleNode = titlePosition !== "none" && title ? (
        <strong
            {...siteBuilderEditorPartAttributes({
                editorMode: runtime.editorMode,
                inlineText: true,
                part: "title",
            })}
            style={partResizeStyleRecord(block, "title")}
        >
            {richText(block, "title", runtime)}
        </strong>
    ) : null;
    const items = (
            <div
                {...siteBuilderEditorSlotAttributes({
                    editorMode: runtime.editorMode,
                    itemCount: slotItems.length || legacyItems.length,
                    slot: "items",
                })}
                data-builder-empty-slot={
                    runtime.editorMode
                    && slotItems.length === 0
                    && legacyItems.length === 0
                        ? "true"
                        : undefined
                }
                className="site-builder-social-items"
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: cssLength(blockOption(block, "gap")) ?? ".65rem",
                    ...horizontalAlignmentStyle(alignment),
                }}
            >
                {slotItems.length > 0 ? slotItems.map((child, index) => (
                    <SiteBuilderBlockView
                        key={`${child.uuid ?? child.type}:${index}`}
                        block={{
                            ...child,
                            options: {
                                ...block.options,
                                ...child.options,
                            },
                        }}
                        parentType={block.type}
                        path={siteBuilderEditorChildPath(path, "items", child, index)}
                        runtime={runtime}
                        slot="items"
                    />
                )) : legacyItems.map((item, index) => {
                    const part = `items:${index}`;
                    const visualStyle = socialLinkOptionStyle({
                        type: "socialLink",
                        content: {
                            href: item.href,
                            icon: `social:${item.label.toLowerCase()}`,
                            label: item.label,
                        },
                        options: block.options,
                    });

                        return (
                            <span
                                key={`${item.label}:${item.href}:${index}`}
                                {...siteBuilderEditorPartAttributes({
                                editorMode: runtime.editorMode,
                                inlineText: true,
                                part,
                                slot: "items",
                            })}
                            data-builder-icon-part={
                                runtime.editorMode ? part : undefined
                            }
                            data-builder-social-link-part={
                                runtime.editorMode && block.type === "socialMedia"
                                    ? part
                                    : undefined
                            }
                            data-builder-share-icon-part={
                                runtime.editorMode && block.type === "share"
                                    ? part
                                    : undefined
                            }
                            {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                            style={{
                                ...visualStyle,
                                ...partResizeStyleRecord(block, part),
                            }}
                        >
                            {link(
                                item.href,
                                `site-builder-social-link is-link-${blockOptionText(
                                    block,
                                    block.type === "share"
                                        ? "shareLinkStyle"
                                        : "socialLinkStyle",
                                    "noUnderline",
                                )}`,
                                siteBuilderIconGlyph(`social:${item.label.toLowerCase()}`),
                                runtime,
                                {
                                    alignItems: "center",
                                    color: "inherit",
                                    display: "inline-grid",
                                    height: "100%",
                                    justifyContent: "center",
                                    textDecoration: "inherit",
                                    width: "100%",
                                },
                            )}
                        </span>
                    );
                })}
            </div>
    );

    return (
        <div
            {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
            className="site-builder-social"
            style={{
                ...styleRecord(block),
                alignItems: titlePosition === "left" ? "center" : undefined,
                display: titlePosition === "left" ? "flex" : "grid",
                flexWrap: titlePosition === "left" ? "wrap" : undefined,
                gap: cssLength(blockOption(block, "gap")) ?? ".75rem",
            }}
        >
            {titleNode}
            {items}
        </div>
    );
}

function SiteBuilderBlockView({
    block,
    lockedColumnParentPart,
    parentType,
    path,
    runtime,
    slot,
}: {
    block: SiteBuilderBlock;
    lockedColumnParentPart?: string;
    parentType?: string;
    path: string;
    runtime: SiteBuilderRuntime;
    slot?: string;
}) {
    if (
        booleanValue(blockOption(block, "requiresBookingPortal"))
        && !siteHasBookingPortal(runtime.site)
    ) {
        return null;
    }

    if (
        block.type === "clientPortalButton"
        && !runtime.editorMode
        && !runtime.site?.clientPortal
    ) {
        return null;
    }

    const blockRegion = region(block) || (block.type === "footer" ? "footer" : "");
    const animation = blockOptionText(block, "animation");
    const commonProps = {
        ...siteBuilderEditorBlockAttributes({
            block,
            editorMode: runtime.editorMode,
            path,
            slot,
        }),
        ...siteBuilderEditorNestedBlockAttributes({
            block,
            editorMode: runtime.editorMode,
            lockedColumnParentPart,
        }),
        ...siteBuilderInteractionAttributes(block),
        "data-site-builder-block": block.type,
        "data-web-region": blockRegion || undefined,
        "data-animation": animation || undefined,
        "data-document-role": blockOptionText(block, "documentRole") || undefined,
        "data-runtime-role": runtimeRole(block) || undefined,
        style: siteBuilderInteractionStyle(block),
    };

    if (block.type === BOOKING_PORTAL_BLOCK_TYPE) {
        return (
            <div {...commonProps} className="site-builder-booking-portal" style={styleRecord(block)}>
                <BookingPortalBlock
                    block={block}
                    bookingDemoData={runtime.bookingDemoData}
                    editorMode={runtime.editorMode}
                    languageCode={runtime.page?.languageCode ?? runtime.site?.defaultLanguageCode}
                    portal={runtime.site?.bookingPortal ?? null}
                    readOnly={runtime.readOnly}
                />
            </div>
        );
    }

    if (block.type === "richContent" || block.type === "footer") {
        const isFooter = block.type === "footer";
        const footerVariant = resolveWebFooterVariant(block);
        const footerUsesLayoutSkeleton = webFooterUsesLayoutSkeleton(block);
        const stackOnMobile = siteBuilderBlockStacksOnMobile(block);
        const gridBlocks = blocksInSlot(block, "grid");
        const contentBlocks = blocksInSlot(block, "content");
        const footerExtendedBlocks = Object.fromEntries(
            WEB_FOOTER_EXTENDED_SLOT_KEYS.map((slotKey) => [
                slotKey,
                blocksInSlot(block, slotKey),
            ]),
        ) as Record<WebFooterExtendedSlotKey, SiteBuilderBlock[]>;
        const footerExtendedSlots = {
            aboveDivider: isFooter
                && footerExtendedBlocks.aboveDivider.length > 0,
            belowDivider: isFooter
                && footerExtendedBlocks.belowDivider.length > 0,
        } satisfies WebFooterExtendedSlotState;
        const footerHasExtendedContent = WEB_FOOTER_EXTENDED_SLOT_KEYS.some(
            (slotKey) => footerExtendedBlocks[slotKey].length > 0,
        );
        const legacyChildren = block.children ?? [];
        const gridColumns = Math.max(
            1,
            numberValue(blockOption(block, "gridColumns"), 12),
        );
        const gridRowHeight = cssLength(blockOption(block, "gridRowHeight")) ?? "48px";
        const role = runtimeRole(block);
        const hasRuntimeContent = isRuntimeNavigationBlock(block) || role === "headerActions";
        const hasStoredContent = gridBlocks.length > 0
            || contentBlocks.length > 0
            || footerHasExtendedContent
            || legacyChildren.length > 0;
        const renderSlot = (
            nestedSlot: "children"
                | "content"
                | "grid"
                | WebFooterExtendedSlotKey,
            blocks: SiteBuilderBlock[],
            runtimeContent: ReactNode = null,
            trailingContent: ReactNode = null,
        ) => {
            const isFooterExtendedSlot = isFooter
                && WEB_FOOTER_EXTENDED_SLOT_KEYS.includes(
                    nestedSlot as WebFooterExtendedSlotKey,
                );
            const isGridSlot = nestedSlot === "grid"
                || isFooterExtendedSlot;
            const hasAuthoredGridColumns = hasPositiveGridNumberOption(block, "gridColumns");
            const hasAuthoredGridRows = hasGridLengthOption(block, "gridRowHeight");
            const hasPlacedGrid = (
                isFooter && nestedSlot === "grid"
            ) || blocks.some((child) => (
                ["gridX", "gridY", "gridW", "gridH"].some(
                    (key) => hasPositiveGridNumberOption(child, key),
                )
            ));
            const isEmpty = blocks.length === 0
                && runtimeContent === null
                && trailingContent === null;
            const footerExtendedStyle = isFooterExtendedSlot
                ? webFooterExtendedSlotGridItemStyle(
                    footerVariant,
                    nestedSlot as WebFooterExtendedSlotKey,
                    footerExtendedSlots,
                )
                : {};
            const footerExtendedOrder = isFooterExtendedSlot
                ? gridDocumentOrder(footerExtendedStyle)
                : undefined;
            const childStyle = (
                child: SiteBuilderBlock,
                index: number,
            ): StyleRecord => {
                if (!isFooter || nestedSlot !== "grid") {
                    return gridItemStyle(child);
                }

                const layoutStyle = webFooterGridItemStyle(
                    child,
                    footerVariant,
                    webFooterFallbackIndex(blocks, index),
                    footerUsesLayoutSkeleton,
                    footerExtendedSlots,
                );

                return {
                    ...layoutStyle,
                    order: gridDocumentOrder(layoutStyle),
                    ...webFooterGridItemPresentationStyle(
                        child,
                        footerVariant,
                    ),
                };
            };

            const renderedContent = runtimeContent ?? blocks.map((child, index) => {
                const placementStyle = authoredGridItemStyle(
                    child,
                    childStyle(child, index),
                );
                const hasAuthoredColumn = ["gridX", "gridW"].some(
                    (key) => hasPositiveGridNumberOption(child, key),
                ) && placementStyle.gridColumn !== undefined;
                const hasAuthoredRow = ["gridY", "gridH"].some(
                    (key) => hasPositiveGridNumberOption(child, key),
                ) && placementStyle.gridRow !== undefined;

                return (
                    <div
                        key={`${nestedSlot}:${child.uuid ?? child.type}:${index}`}
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            part: `slot:${nestedSlot}`,
                            slot: nestedSlot,
                        })}
                        className="site-builder-grid-item"
                        data-site-builder-authored-grid-placement={
                            hasAuthoredColumn || hasAuthoredRow ? "true" : undefined
                        }
                        data-site-builder-authored-grid-column={hasAuthoredColumn ? "true" : undefined}
                        data-site-builder-authored-grid-row={hasAuthoredRow ? "true" : undefined}
                        style={placementStyle}
                    >
                        <SiteBuilderBlockView
                            block={child}
                            parentType={block.type}
                            path={siteBuilderEditorChildPath(
                                path,
                                nestedSlot,
                                child,
                                index,
                            )}
                            runtime={runtime}
                            slot={nestedSlot}
                        />
                    </div>
                );
            });
            const renderedSlotChildren = Children.toArray([
                renderedContent,
                trailingContent,
            ]);
            const resolvedGridRows = isFooter
                ? nestedSlot === "grid"
                    ? webFooterGridRowCount(
                        blocks,
                        footerVariant,
                        footerUsesLayoutSkeleton,
                        footerExtendedSlots,
                    )
                    : gridRowCount(blocks)
                : gridRowCount(blocks);

            return (
                <div
                    key={nestedSlot}
                    {...siteBuilderEditorSlotAttributes({
                        editorMode: runtime.editorMode,
                        gridColumns: isGridSlot ? gridColumns : undefined,
                        gridRows: isGridSlot ? resolvedGridRows : undefined,
                        itemCount: blocks.length,
                        slot: nestedSlot,
                    })}
                    className={[
                        "site-builder-grid",
                        hasPlacedGrid ? "site-builder-grid-placed" : "",
                        isFooterExtendedSlot
                            ? "site-builder-grid-item site-builder-footer-extended-slot"
                            : "",
                    ].filter(Boolean).join(" ")}
                    data-builder-empty-slot={runtime.editorMode && isEmpty ? "true" : undefined}
                    data-site-builder-authored-grid={
                        hasAuthoredGridColumns || hasAuthoredGridRows ? "true" : undefined
                    }
                    data-site-builder-authored-grid-columns={hasAuthoredGridColumns ? "true" : undefined}
                    data-site-builder-authored-grid-rows={hasAuthoredGridRows ? "true" : undefined}
                    data-site-builder-stack-on-mobile={stackOnMobile ? "true" : "false"}
                    data-web-footer-extended-slot={
                        isFooterExtendedSlot ? nestedSlot : undefined
                    }
                    style={{
                        ...footerExtendedStyle,
                        order: isFooterExtendedSlot
                            ? footerExtendedOrder === undefined
                                ? undefined
                                : footerExtendedOrder - 1
                            : undefined,
                        gap: cssLength(blockOption(block, "gap")) ?? undefined,
                        minHeight: runtime.editorMode && isEmpty
                            ? "44px"
                            : undefined,
                        position: isFooter
                            && nestedSlot === "grid"
                            && runtime.editorMode
                            ? "relative"
                            : undefined,
                        gridTemplateColumns: hasPlacedGrid || isGridSlot
                            ? `repeat(${gridColumns}, minmax(0, 1fr))`
                            : undefined,
                        gridAutoRows: hasPlacedGrid || isGridSlot
                            ? `minmax(${gridRowHeight}, auto)`
                            : undefined,
                        "--site-builder-authored-grid-columns": hasAuthoredGridColumns
                            ? `repeat(${gridColumns}, minmax(0, 1fr))`
                            : undefined,
                        "--site-builder-authored-grid-rows": hasAuthoredGridRows
                            ? `minmax(${gridRowHeight}, auto)`
                            : undefined,
                    } as StyleRecord}
                >
                    {renderedSlotChildren}
                </div>
            );
        };
        const renderEmptyFooterExtendedSlot = (
            nestedSlot: WebFooterExtendedSlotKey,
        ) => {
            const gridPlacement = webFooterExtendedSlotGridItemStyle(
                footerVariant,
                nestedSlot,
                footerExtendedSlots,
            );
            const gridOrder = gridDocumentOrder(gridPlacement);

            return (
                <div
                    key={`${nestedSlot}:empty-editor-slot`}
                    {...siteBuilderEditorSlotAttributes({
                        editorMode: true,
                        gridColumns,
                        gridRows: 1,
                        itemCount: 0,
                        slot: nestedSlot,
                    })}
                    aria-hidden="true"
                    className="site-builder-grid site-builder-grid-placed site-builder-grid-item site-builder-footer-extended-slot site-builder-footer-empty-extended-slot"
                    data-builder-empty-slot="true"
                    data-site-builder-stack-on-mobile={stackOnMobile ? "true" : "false"}
                    data-web-footer-extended-slot={nestedSlot}
                    style={{
                        ...gridPlacement,
                        gap: cssLength(blockOption(block, "gap")) ?? undefined,
                        gridAutoRows: `minmax(${gridRowHeight}, auto)`,
                        gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                        height: gridRowHeight,
                        left: 0,
                        minHeight: "44px",
                        order: gridOrder === undefined ? undefined : gridOrder - 1,
                        pointerEvents: "none",
                        position: "absolute",
                        right: 0,
                        top: 0,
                        zIndex: 2,
                    }}
                />
            );
        };
        const gridRuntimeContent = isRuntimeNavigationBlock(block)
            ? navigation(block, runtime)
            : null;
        const gridTrailingContent = role === "headerActions"
            ? runtime.headerActions ?? null
            : null;
        const footerAboveDividerSlot = isFooter
            ? footerExtendedSlots.aboveDivider
                ? renderSlot(
                    "aboveDivider",
                    footerExtendedBlocks.aboveDivider,
                )
                : runtime.editorMode
                    ? renderEmptyFooterExtendedSlot("aboveDivider")
                    : null
            : null;
        const footerBelowDividerSlot = isFooter
            ? footerExtendedSlots.belowDivider
                ? renderSlot(
                    "belowDivider",
                    footerExtendedBlocks.belowDivider,
                )
                : runtime.editorMode
                    ? renderEmptyFooterExtendedSlot("belowDivider")
                    : null
            : null;
        const mainGridTrailingContent = isFooter
            ? (
                <Fragment key={`${block.uuid ?? path}:footer-extended-slots`}>
                    {footerAboveDividerSlot}
                    {footerBelowDividerSlot}
                    {gridTrailingContent}
                </Fragment>
            )
            : gridTrailingContent;

        const Container = isFooter ? "footer" : "section";

        return (
            <Container
                {...commonProps}
                id={blockOptionText(block, "anchorId") || blockOptionText(block, "id") || undefined}
                className={`site-builder-section site-builder-region-${blockRegion || "content"}`}
                data-web-footer-variant={isFooter ? footerVariant : undefined}
                style={styleRecord(block)}
            >
                {gridBlocks.length > 0
                || footerHasExtendedContent
                || hasRuntimeContent
                || !hasStoredContent
                    ? renderSlot(
                        "grid",
                        gridBlocks,
                        gridRuntimeContent,
                        mainGridTrailingContent,
                    )
                    : null}
                {!isFooter && contentBlocks.length > 0
                    ? renderSlot("content", contentBlocks)
                    : null}
                {legacyChildren.length > 0
                    ? renderSlot("children", legacyChildren)
                    : null}
            </Container>
        );
    }

    if (block.type === "column") {
        const layout = blockOptionText(block, "contentLayout");
        const inlineLayout = layout === "horizontal"
            || layout === "inline"
            || layout === "inlineActions";
        const horizontalAlignment = blockFieldText(
            block,
            "options",
            "alignment",
        );
        const contentItemCount = blocksInSlot(block, "content").length
            + (block.children?.length ?? 0);

        return (
            <div
                {...commonProps}
                {...siteBuilderEditorColumnAttributes({
                    editorMode: runtime.editorMode,
                    part: path,
                })}
                className={`site-builder-column ${inlineLayout ? "site-builder-column-inline" : ""}`}
                data-builder-slot-container={runtime.editorMode ? "content" : undefined}
                data-builder-slot-item-count={runtime.editorMode ? contentItemCount : undefined}
                data-site-builder-stack-on-mobile={
                    siteBuilderBlockStacksOnMobile(block) ? "true" : "false"
                }
                style={{
                    ...styleRecord(block),
                    ...(inlineLayout
                        ? horizontalAlignmentStyle(horizontalAlignment)
                        : {
                            justifyItems: horizontalAlignment === "center"
                                ? "center"
                                : horizontalAlignment === "right"
                                    || horizontalAlignment === "end"
                                    ? "end"
                                    : horizontalAlignment === "left"
                                        || horizontalAlignment === "start"
                                        ? "start"
                                        : undefined,
                        }),
                }}
            >
                {nestedBlocks(block, runtime, path)}
            </div>
        );
    }

    if (
        block.type === "text"
        || block.type === "paragraph"
        || block.type === "emoji"
        || block.type === "signature"
    ) {
        return <div {...commonProps}>{textElement(block, runtime)}</div>;
    }

    if (block.type === "heading") {
        const key = block.content?.title !== undefined ? "title" : "text";
        const level = Math.max(1, Math.min(3, numberValue(block.options?.level, 2)));
        const Heading = (level === 1 ? "h1" : level === 3 ? "h3" : "h2") as "h1" | "h2" | "h3";

        return (
            <Heading
                {...commonProps}
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: key,
                })}
                {...siteBuilderEditorInlinePlaceholderAttributes({
                    block,
                    editorMode: runtime.editorMode,
                    empty: inlineTextIsEmpty(block, key),
                })}
                className={`site-builder-text site-builder-text-h${level}`}
                style={{
                    margin: 0,
                    maxWidth: "100%",
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, key),
                }}
            >
                {richText(block, key, runtime)}
            </Heading>
        );
    }

    if (block.type === "quote") {
        const key = block.content?.quote !== undefined ? "quote" : "text";

        return (
            <blockquote
                {...commonProps}
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: key,
                })}
                {...siteBuilderEditorInlinePlaceholderAttributes({
                    block,
                    editorMode: runtime.editorMode,
                    empty: inlineTextIsEmpty(block, key),
                })}
                className="site-builder-blockquote"
                style={{
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, key),
                }}
            >
                {richText(block, key, runtime)}
            </blockquote>
        );
    }

    if (block.type === "list" || block.type === "checklist") {
        const items = firstBlockText(block, ["items", "text"])
            .split("\n")
            .filter(Boolean);
        const renderedItems = items.length > 0
            ? items
            : runtime.editorMode
                ? ["\u200b"]
                : [];
        const numbered = blockOptionText(block, "listStyle") === "numbered";
        const List = numbered ? "ol" : "ul";

        return (
            <List
                {...commonProps}
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: "items",
                })}
                {...siteBuilderEditorInlinePlaceholderAttributes({
                    block,
                    editorMode: runtime.editorMode,
                    empty: items.length === 0,
                })}
                className={`site-builder-content-list${block.type === "checklist" ? " is-checklist" : ""}`}
                style={{
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, "items"),
                }}
            >
                {renderedItems.map((item, index) => (
                    <li key={`${item}:${index}`}>
                        {block.type === "checklist" ? "✓ " : null}
                        {item}
                    </li>
                ))}
            </List>
        );
    }

    if (block.type === "table") {
        const rows = firstBlockText(block, ["items", "text"])
            .split("\n")
            .map((row) => row.split(/\s*\|\s*|\t/u))
            .filter((row) => row.some((cell) => cell.trim() !== ""));

        return (
            <div {...commonProps} style={{ overflowX: "auto", ...styleRecord(block) }}>
                <table
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "items",
                    })}
                    className="site-builder-table"
                    style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        ...partResizeStyleRecord(block, "items"),
                    }}
                >
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={`${row.join("|")}:${rowIndex}`}>
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={`${cell}:${cellIndex}`}
                                        style={{
                                            border: "1px solid var(--site-border)",
                                            padding: ".65rem .8rem",
                                        }}
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (block.type === "uploadFile") {
        const source = firstBlockText(block, ["file", "url", "href"]);
        const label = firstBlockText(block, ["label", "title"], "File");

        if (!source) {
            return runtime.editorMode ? (
                <div
                    {...commonProps}
                    className="site-builder-upload-placeholder"
                    style={{
                        border: "1px dashed var(--site-border)",
                        borderRadius: "var(--site-radius, 16px)",
                        padding: "1rem",
                        ...styleRecord(block),
                    }}
                >
                    {label}
                </div>
            ) : null;
        }

        return (
            <div {...commonProps}>
                {link(source, "site-builder-upload-link", label, runtime, styleRecord(block), true)}
            </div>
        );
    }

    if (block.type === "link") {
        const key = block.content?.label !== undefined ? "label" : "text";
        const href = firstBlockText(block, ["href", "url"], "#");

        return (
            <div {...commonProps}>
                {link(
                    authoredBlockHref(block, href),
                    "site-builder-link",
                    <span
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: key,
                        })}
                        {...siteBuilderEditorInlinePlaceholderAttributes({
                            block,
                            editorMode: runtime.editorMode,
                            empty: inlineTextIsEmpty(block, key),
                        })}
                        style={partResizeStyleRecord(block, key)}
                    >
                        {richText(block, key, runtime)}
                    </span>,
                    runtime,
                    styleRecord(block),
                    undefined,
                    true,
                )}
            </div>
        );
    }

    if (block.type === "image" || block.type === "media") {
        return (
            <div
                {...commonProps}
                style={{
                    display: "flex",
                    ...horizontalAlignmentStyle(blockFieldText(
                        block,
                        "options",
                        "alignment",
                    )),
                    ...styleRecord(block),
                }}
            >
                {media(block, runtime)}
            </div>
        );
    }

    if (block.type === "button" || block.type === "clientPortalButton" || block.type === "ctaBadge" || block.type === "donationButton" || block.type === "addToCartButton") {
        const label = firstBlockText(
            block,
            ["label", "text"],
            block.type === "button" || block.type === "clientPortalButton" ? "Open" : "",
        );
        const href = block.type === "clientPortalButton"
            ? "{{clientPortal.url}}"
            : firstBlockText(block, ["href", "url"], "#");
        const labelKey = block.content?.label !== undefined ? "label" : "text";
        const labelContent = richText(block, labelKey, runtime, label);
        const title = block.type === "ctaBadge" ? blockText(block, "title") : "";
        const icon = blockText(block, "icon");
        const buttonPreset = blockOptionText(block, "buttonPreset", "primary");
        const configuredStyle = styleRecord(block);
        const buttonTheme = buttonPreset === "secondary"
            ? "outline"
            : buttonPreset === "custom"
                ? ""
                : runtime.design.components.buttons;

        return (
            <div
                {...commonProps}
                data-site-builder-product-uuid={
                    block.type === "addToCartButton"
                        ? blockText(block, "productUuid") || undefined
                        : undefined
                }
                style={{
                    display: "flex",
                    width: "100%",
                    ...horizontalAlignmentStyle(blockFieldText(
                        block,
                        "options",
                        "alignment",
                    )),
                }}
            >
                {link(
                    authoredBlockHref(block, href),
                    `site-builder-button ${buttonTheme ? siteBuilderButtonClass(buttonTheme) : "site-builder-button-custom"}`,
                    <>
                        {block.type === "ctaBadge"
                            ? nestedBlocks(block, runtime, path)
                            : null}
                        {icon ? (
                            <span
                                aria-hidden="true"
                                contentEditable={false}
                                data-builder-icon-part={runtime.editorMode ? "icon" : undefined}
                                style={partResizeStyleRecord(block, "icon")}
                            >
                                {siteBuilderIconGlyph(icon)}
                            </span>
                        ) : null}
                        {title ? (
                            <span
                                {...siteBuilderEditorPartAttributes({
                                    editorMode: runtime.editorMode,
                                    inlineText: true,
                                    part: "title",
                                })}
                                className="site-builder-cta-title"
                                style={partResizeStyleRecord(block, "title")}
                            >
                                {richText(block, "title", runtime)}
                            </span>
                        ) : null}
                        {label || runtime.editorMode ? (
                            <span
                                {...siteBuilderEditorPartAttributes({
                                    editorMode: runtime.editorMode,
                                    inlineText: true,
                                    part: labelKey,
                                })}
                                {...siteBuilderEditorInlinePlaceholderAttributes({
                                    block,
                                    editorMode: runtime.editorMode,
                                    empty: inlineTextIsEmpty(block, labelKey),
                                })}
                                className="site-builder-cta-label"
                                style={partResizeStyleRecord(block, labelKey)}
                            >
                                {labelContent}
                            </span>
                        ) : null}
                    </>,
                    runtime,
                    {
                        ...buttonOptionStyle(block),
                        ...configuredStyle,
                        ...(buttonPreset === "custom"
                            ? {
                                backgroundColor: configuredStyle.backgroundColor ?? "transparent",
                                color: configuredStyle.color ?? "inherit",
                            }
                            : {}),
                    },
                    optionalBooleanValue(blockOption(block, "openInNewWindow")),
                    true,
                )}
            </div>
        );
    }

    if (block.type === "badge") {
        const labelContent = richText(block, "label", runtime, firstBlockText(block, ["text", "title"]));
        const href = firstBlockText(block, ["href", "url"]);
        const content = (
            <>
                {nestedBlocks(block, runtime, path)}
                <span
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "label",
                    })}
                    style={partResizeStyleRecord(block, "label")}
                >
                    {labelContent}
                </span>
            </>
        );
        const badgeStyle = {
            alignItems: "center",
            display: "inline-flex",
            justifyContent: "center",
            width: "fit-content",
            ...styleRecord(block),
            ...badgeOptionStyle(block),
            ...alignedInlineItemStyle(block),
        };

        return href ? (
            <span
                {...commonProps}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                className="site-builder-badge"
                style={badgeStyle}
            >
                {link(
                    authoredBlockHref(block, href),
                    "site-builder-badge-link",
                    content,
                    runtime,
                    {
                        color: "inherit",
                        textDecoration: "none",
                    },
                    optionalBooleanValue(blockOption(block, "openInNewWindow")),
                )}
            </span>
        ) : (
            <span
                {...commonProps}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                className="site-builder-badge"
                style={badgeStyle}
            >
                {content}
            </span>
        );
    }

    if (block.type === "card") {
        const contentBlocks = blocksInSlot(block, "content");
        const imageUrl = firstBlockText(block, ["imageUrl", "src"]);
        const title = firstBlockText(block, ["title", "label"]);
        const text = firstBlockText(block, ["text", "description"]);
        const actionLabel = firstBlockText(block, ["actionLabel", "buttonLabel"]);
        const actionHref = firstBlockText(block, ["href", "url"], "#");
        const layout = blockOptionText(block, "contentLayout", blockOptionText(block, "imagePosition", "top"));
        const horizontalLayout = layout === "left" || layout === "right";
        const variant = blockOptionText(block, "cardVariant", "default");
        const imageNode = imageUrl ? (
            <div
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    part: "image",
                })}
                className="site-builder-card-media"
                style={partResizeStyleRecord(block, "image")}
            >
                <img
                    src={imageUrl}
                    alt={blockText(block, "alt", title)}
                    title={blockText(block, "imageTitle") || undefined}
                    className="site-builder-image"
                    loading={booleanValue(blockOption(block, "eager")) ? "eager" : "lazy"}
                    style={{
                        aspectRatio: siteBuilderAspectRatio(
                            blockOptionText(block, "aspectRatio"),
                        ),
                        height: horizontalLayout ? "100%" : undefined,
                        objectFit: blockOptionText(block, "imageObjectFit", "cover") as CSSProperties["objectFit"],
                        width: "100%",
                    }}
                />
            </div>
        ) : null;
        const contentNode = (
            <div
                {...siteBuilderEditorSlotAttributes({
                    editorMode: runtime.editorMode,
                    itemCount: contentBlocks.length,
                    slot: "content",
                })}
                className={`site-builder-card-content ${layout === "inline" || layout === "inlineActions" ? "site-builder-card-content-inline" : ""}`}
                style={{
                    alignItems: layout === "inline" || layout === "inlineActions"
                        ? "center"
                        : undefined,
                    display: layout === "inline" || layout === "inlineActions"
                        ? "flex"
                        : "grid",
                    gap: cssLength(blockOption(block, "gap")) ?? ".8rem",
                    justifyContent: layout === "inline" || layout === "inlineActions"
                        ? "space-between"
                        : undefined,
                    minWidth: 0,
                }}
            >
                {contentBlocks.length > 0 ? contentBlocks.map((child, index) => (
                    <SiteBuilderBlockView
                        key={`${child.uuid ?? child.type}:${index}`}
                        block={child}
                        parentType={block.type}
                        path={siteBuilderEditorChildPath(path, "content", child, index)}
                        runtime={runtime}
                        slot="content"
                    />
                )) : (
                    <>
                        {title ? (
                            <h3
                                {...siteBuilderEditorPartAttributes({
                                    editorMode: runtime.editorMode,
                                    inlineText: true,
                                    part: block.content?.title !== undefined ? "title" : "label",
                                })}
                                className="site-builder-card-title"
                                style={partResizeStyleRecord(
                                    block,
                                    block.content?.title !== undefined ? "title" : "label",
                                )}
                            >
                                {richText(block, block.content?.title !== undefined ? "title" : "label", runtime, title)}
                            </h3>
                        ) : null}
                        {text ? (
                            <p
                                {...siteBuilderEditorPartAttributes({
                                    editorMode: runtime.editorMode,
                                    inlineText: true,
                                    part: block.content?.text !== undefined ? "text" : "description",
                                })}
                                className="site-builder-card-text"
                                style={partResizeStyleRecord(
                                    block,
                                    block.content?.text !== undefined ? "text" : "description",
                                )}
                            >
                                {richText(block, block.content?.text !== undefined ? "text" : "description", runtime, text)}
                            </p>
                        ) : null}
                        {actionLabel ? (
                            <div className="site-builder-card-action">
                                {link(
                                    authoredBlockHref(block, actionHref),
                                    `site-builder-button ${siteBuilderButtonClass(blockOptionText(block, "buttonPreset", runtime.design.components.buttons))}`,
                                    <span
                                        {...siteBuilderEditorPartAttributes({
                                            editorMode: runtime.editorMode,
                                            inlineText: true,
                                            part: block.content?.actionLabel !== undefined ? "actionLabel" : "buttonLabel",
                                        })}
                                        style={partResizeStyleRecord(
                                            block,
                                            block.content?.actionLabel !== undefined ? "actionLabel" : "buttonLabel",
                                        )}
                                    >
                                        {actionLabel}
                                    </span>,
                                    runtime,
                                )}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );

        return (
            <article
                {...commonProps}
                className={`site-builder-card site-builder-card-${variant} site-builder-card-layout-${layout}`}
                data-card-variant={variant}
                style={{
                    ...cardStyle(block),
                    ...(horizontalLayout
                        ? {
                            alignItems: "stretch",
                            gridTemplateColumns:
                                "minmax(0, .9fr) minmax(0, 1.1fr)",
                        }
                        : {}),
                }}
            >
                {layout === "bottom" || layout === "right" ? contentNode : imageNode}
                {layout === "bottom" || layout === "right" ? imageNode : contentNode}
            </article>
        );
    }

    if (block.type === "separator") {
        const configured = styleRecord(block);
        const separatorStyle = blockOptionText(block, "separatorStyle", "solid");
        const alignment = blockOptionText(block, "alignment");

        return (
            <hr
                {...commonProps}
                className="site-builder-separator"
                style={{
                    ...configured,
                    borderBottom: 0,
                    borderLeft: 0,
                    borderRight: 0,
                    borderTopColor: configured.borderColor ?? "currentColor",
                    borderTopStyle: (
                        separatorStyle === "dashed"
                        || separatorStyle === "dotted"
                        || separatorStyle === "double"
                    ) ? separatorStyle : "solid",
                    borderTopWidth: configured.borderWidth ?? "1px",
                    marginLeft: alignment === "right"
                        ? "auto"
                        : alignment === "center"
                            ? "auto"
                            : configured.marginLeft,
                    marginRight: alignment === "left"
                        ? "auto"
                        : alignment === "center"
                            ? "auto"
                            : configured.marginRight,
                }}
            />
        );
    }

    if (block.type === "form") {
        return <div {...commonProps}>{form(block, runtime, path)}</div>;
    }

    if (block.type === "field") {
        const label = firstBlockText(block, ["label", "text"], "Field");
        const placeholder = blockText(block, "placeholder");
        const defaultValue = blockText(block, "defaultValue");
        const description = blockText(block, "description");
        const showDescription = optionalBooleanValue(
            blockOption(block, "showDescription"),
        ) !== false;
        const inputType = blockOptionText(block, "fieldInputType", "text");
        const commonInputProps = {
            defaultValue: defaultValue || undefined,
            disabled: runtime.editorMode || runtime.readOnly,
            name: blockText(block, "name") || block.uuid || undefined,
            placeholder: placeholder || undefined,
            required: blockOptionText(block, "requirement") === "required",
        };
        const inputStyle: CSSProperties = {
            background: "var(--site-background, #fff)",
            border: "1px solid var(--site-border)",
            borderRadius: "var(--site-radius, 12px)",
            boxSizing: "border-box",
            color: "var(--site-text)",
            fontFamily: "inherit",
            fontSize: ".875rem",
            maxWidth: "100%",
            minHeight: "44px",
            minWidth: 0,
            outline: "none",
            padding: ".75rem 1rem",
            width: "100%",
            ...partResizeStyleRecord(block, "input"),
        };
        const inputPartAttributes = siteBuilderEditorPartAttributes({
            editorMode: runtime.editorMode,
            part: "input",
        });
        const choices = blockText(block, "choices")
            .split("\n")
            .map((choice) => choice.trim())
            .filter(Boolean);
        const control = inputType === "longText" ? (
            <textarea
                {...commonInputProps}
                {...inputPartAttributes}
                rows={Math.max(2, numberValue(blockOption(block, "rows"), 3))}
                style={inputStyle}
            />
        ) : inputType === "selection" ? (
            <select {...commonInputProps} {...inputPartAttributes} style={inputStyle}>
                <option value="">{placeholder}</option>
                {choices.map((choice) => <option key={choice}>{choice}</option>)}
            </select>
        ) : (
            <input
                {...commonInputProps}
                {...inputPartAttributes}
                type={inputType === "telephone" ? "tel" : inputType === "datetime" ? "datetime-local" : inputType}
                style={inputStyle}
            />
        );

        return (
            <label
                {...commonProps}
                className="site-builder-form-field"
                style={{
                    display: "grid",
                    gap: ".25rem",
                    gridColumn: parentType === "form" && inputType === "longText"
                        ? "1 / -1"
                        : undefined,
                    maxWidth: "100%",
                    minWidth: 0,
                    ...styleRecord(block),
                }}
            >
                <span
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "label",
                    })}
                    style={{
                        fontSize: ".875rem",
                        fontWeight: 500,
                        ...partResizeStyleRecord(block, "label"),
                    }}
                >
                    {richText(block, "label", runtime, label)}
                </span>
                {control}
                {description && showDescription ? (
                    <small
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: "description",
                        })}
                        style={partResizeStyleRecord(block, "description")}
                    >
                        {richText(block, "description", runtime, description)}
                    </small>
                ) : null}
            </label>
        );
    }

    if (block.type === "submitButton") {
        return (
            <div
                {...commonProps}
                className="site-builder-form-submit"
                style={{
                    display: "flex",
                    gridColumn: parentType === "form" ? "1 / -1" : undefined,
                    ...horizontalAlignmentStyle(blockOptionText(block, "buttonPosition", "right")),
                    ...styleRecord(block),
                }}
            >
                <button
                    className={`site-builder-button ${siteBuilderButtonClass(runtime.design.components.buttons)}`}
                    disabled={runtime.editorMode || runtime.readOnly}
                    type="submit"
                >
                    <span
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: "label",
                        })}
                        style={partResizeStyleRecord(block, "label")}
                    >
                        {richText(block, "label", runtime, "Submit")}
                    </span>
                </button>
            </div>
        );
    }

    if (block.type === "map") {
        const renderedMap = map(block, runtime);

        return (
            <div {...commonProps}>
                {renderedMap ?? (runtime.editorMode ? (
                    <div
                        {...siteBuilderEditorPartAttributes({
                            editorMode: true,
                            part: "map",
                        })}
                        {...siteBuilderEditorOutlineOwnerAttributes(true)}
                        className="site-builder-map-placeholder"
                        data-site-builder-map-placeholder="true"
                        style={{
                            alignItems: "center",
                            border: "1px dashed var(--site-border)",
                            borderRadius: "var(--site-radius, 16px)",
                            display: "grid",
                            minHeight: "10rem",
                            placeItems: "center",
                            width: "100%",
                            ...styleRecord(block),
                            ...partResizeStyleRecord(block, "map"),
                        }}
                    >
                        Map
                    </div>
                ) : null)}
            </div>
        );
    }

    if (block.type === "socialLink") {
        const labelKey = block.content?.label !== undefined ? "label" : "text";
        const label = firstBlockText(block, ["label", "text"], "Social");
        const href = firstBlockText(block, ["href", "url"]);
        const icon = firstBlockText(block, ["icon"], `social:${socialNetwork(block)}`);
        const visualStyle = {
            ...socialLinkOptionStyle(block),
            ...styleRecord(block),
        };
        const className = `site-builder-social-link is-link-${blockOptionText(
            block,
            parentType === "share"
                ? "shareLinkStyle"
                : "socialLinkStyle",
            "noUnderline",
        )}`;
        const content = (
            <span
                {...commonProps}
                {...siteBuilderEditorOutlineOwnerAttributes(runtime.editorMode)}
                data-builder-social-link-part={
                    runtime.editorMode && parentType === "socialMedia"
                        ? path
                        : undefined
                }
                data-builder-share-icon-part={
                    runtime.editorMode && parentType === "share"
                        ? path
                        : undefined
                }
                style={{
                    ...visualStyle,
                    alignItems: "center",
                    display: "grid",
                    justifyContent: "center",
                    position: "relative",
                }}
                title={label}
            >
                <span
                    aria-hidden="true"
                    contentEditable={false}
                    data-builder-icon-part={runtime.editorMode ? "icon" : undefined}
                    style={partResizeStyleRecord(block, "icon")}
                >
                    {siteBuilderIconGlyph(icon)}
                </span>
                <span
                    style={{
                        clip: "rect(0 0 0 0)",
                        clipPath: "inset(50%)",
                        height: 1,
                        overflow: "hidden",
                        position: "absolute",
                        whiteSpace: "nowrap",
                        width: 1,
                    }}
                >
                    {richText(block, labelKey, runtime, label)}
                </span>
            </span>
        );
        const linkStyle = {
            alignItems: "center",
            color: "inherit",
            display: "inline-flex",
            textDecoration: "inherit",
        } satisfies CSSProperties;

        if (parentType === "share") {
            return (
                <SiteBuilderShareAnchor
                    className={className}
                    editorMode={runtime.editorMode}
                    explicitHref={href}
                    network={siteBuilderShareNetwork(`${icon} ${label} ${href}`)}
                    openInNewWindow={optionalBooleanValue(
                        blockOption(block, "openInNewWindow"),
                    )}
                    shareTitle={runtime.page?.title ?? runtime.site?.name ?? label}
                    style={linkStyle}
                >
                    {content}
                </SiteBuilderShareAnchor>
            );
        }

        return link(
            authoredBlockHref(block, href || "#"),
            className,
            content,
            runtime,
            linkStyle,
            optionalBooleanValue(blockOption(block, "openInNewWindow")),
        );
    }

    if (block.type === "socialMedia" || block.type === "share") {
        return <div {...commonProps}>{socialLinks(block, runtime, path)}</div>;
    }

    if (block.type === "video") {
        const videoSource = siteVideoSource(firstBlockText(block, ["videoUrl", "url"]));
        const aspectRatio = blockOptionText(block, "aspectRatio");
        const configuredStyle = styleRecord(block);
        const resolvedAspectRatio = siteBuilderAspectRatio(aspectRatio);
        const videoStyle = {
            ...configuredStyle,
            aspectRatio: resolvedAspectRatio,
            ...(resolvedAspectRatio
                ? {
                    height: configuredStyle.height ?? "auto",
                    minHeight: configuredStyle.minHeight ?? 0,
                    width: configuredStyle.width ?? "100%",
                }
                : {}),
            pointerEvents: runtime.editorMode ? "none" : undefined,
        } satisfies CSSProperties;

        return videoSource?.kind === "embed" ? (
            <iframe
                {...commonProps}
                className="site-builder-video"
                src={videoSource.source}
                title={blockText(block, "title", "Video")}
                allowFullScreen
                style={videoStyle}
                tabIndex={runtime.editorMode ? -1 : undefined}
            />
        ) : videoSource?.kind === "file" ? (
            <video
                {...commonProps}
                className="site-builder-video"
                controls
                playsInline
                src={videoSource.source}
                style={videoStyle}
                tabIndex={runtime.editorMode ? -1 : undefined}
                title={blockText(block, "title", "Video")}
            />
        ) : runtime.editorMode ? (
            <div
                {...commonProps}
                className="site-builder-video site-builder-video-placeholder"
                style={{
                    alignItems: "center",
                    border: "1px dashed var(--site-border)",
                    borderRadius: "var(--site-radius, 16px)",
                    display: "grid",
                    minHeight: "10rem",
                    placeItems: "center",
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, "icon"),
                }}
            >
                {blockText(block, "title", "Video")}
            </div>
        ) : null;
    }

    if (block.type === "icon") {
        const iconValue = firstBlockText(block, ["icon", "text"], "lucide:check");
        const iconSize = blockFieldText(block, "options", "size");
        const iconStyle = blockOptionText(block, "iconStyle", "plain");
        const href = firstBlockText(block, ["href", "url"]);
        const label = firstBlockText(block, ["label", "title"]);
        const presetFontSize = iconSize === "xs"
            ? "1.75rem"
            : iconSize === "sm"
                ? "2.25rem"
                : iconSize === "lg"
                    ? "3.5rem"
                    : iconSize === "xl"
                        ? "4.25rem"
                        : iconSize === "1x"
                            ? "1rem"
                            : iconSize === "2x"
                                ? "1.25rem"
                                : iconSize === "3x"
                                    ? "1.5rem"
                                    : iconSize === "4x"
                                        ? "2rem"
                                        : iconSize === "5x"
                                            ? "2.5rem"
                                            : "";
        const fontSize = /^\d+(?:\.\d+)?(?:px|rem|em|%)?$/u.test(iconSize)
            ? cssLength(iconSize) ?? "2.75rem"
            : presetFontSize || "2.75rem";

        const iconNode = (
            <span
                {...commonProps}
                className="site-builder-icon"
                data-site-builder-icon-value={iconValue}
                data-builder-icon-part={runtime.editorMode ? "icon" : undefined}
                data-builder-share-icon-part={
                    runtime.editorMode && parentType === "share"
                        ? path
                        : undefined
                }
                style={{
                    alignItems: "center",
                    display: "inline-flex",
                    justifyContent: "center",
                    lineHeight: 1,
                    ...(parentType === "share"
                        ? socialLinkOptionStyle(block)
                        : {}),
                    ...(iconStyle !== "plain"
                        ? {
                            aspectRatio: "1 / 1",
                            backgroundColor: iconStyle === "outline"
                                ? "transparent"
                                : "var(--site-surface)",
                            border: iconStyle === "outline"
                                ? "1px solid currentColor"
                                : undefined,
                            borderRadius: iconStyle === "circle"
                                ? "9999px"
                                : iconStyle === "rounded"
                                    ? "var(--site-radius, 12px)"
                                    : "0",
                            clipPath: iconStyle === "gear"
                                ? "polygon(38% 0, 62% 0, 68% 10%, 80% 15%, 91% 10%, 100% 31%, 92% 39%, 92% 61%, 100% 69%, 91% 90%, 80% 85%, 68% 90%, 62% 100%, 38% 100%, 32% 90%, 20% 85%, 9% 90%, 0 69%, 8% 61%, 8% 39%, 0 31%, 9% 10%, 20% 15%, 32% 10%)"
                                : undefined,
                            padding: ".35em",
                        }
                        : {}),
                    fontSize,
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, "icon"),
                }}
                aria-hidden={label ? undefined : true}
                aria-label={label || undefined}
            >
                {siteBuilderIconGlyph(iconValue)}
            </span>
        );

        if (parentType === "share") {
            return (
                <SiteBuilderShareAnchor
                    className="site-builder-icon-link"
                    editorMode={runtime.editorMode}
                    explicitHref={href}
                    network={siteBuilderShareNetwork(`${iconValue} ${label} ${href}`)}
                    openInNewWindow={optionalBooleanValue(
                        blockOption(block, "openInNewWindow"),
                    )}
                    shareTitle={runtime.page?.title ?? runtime.site?.name ?? label}
                    style={{ color: "inherit", display: "inline-flex", textDecoration: "none" }}
                >
                    {iconNode}
                </SiteBuilderShareAnchor>
            );
        }

        return href ? link(
            authoredBlockHref(block, href),
            "site-builder-icon-link",
            iconNode,
            runtime,
            { color: "inherit", display: "inline-flex", textDecoration: "none" },
            optionalBooleanValue(blockOption(block, "openInNewWindow")),
        ) : iconNode;
    }

    if (block.type === "iconList") {
        const items = firstBlockText(block, ["items", "text"]).split("\n").filter(Boolean);
        const renderedItems = items.length > 0
            ? items
            : runtime.editorMode
                ? ["\u200b"]
                : [];

        return (
            <ul {...commonProps} className="site-builder-icon-list" style={styleRecord(block)}>
                {renderedItems.map((item, index) => (
                    <li
                        data-builder-icon-list-row={runtime.editorMode ? "true" : undefined}
                        key={`${item}:${index}`}
                    >
                        <span
                            {...siteBuilderEditorPartAttributes({
                                editorMode: runtime.editorMode,
                                part: "icon",
                            })}
                            aria-hidden="true"
                            contentEditable={false}
                            data-builder-icon-list-icon={runtime.editorMode ? "true" : undefined}
                            data-builder-icon-part={runtime.editorMode ? "icon" : undefined}
                            style={partResizeStyleRecord(block, "icon")}
                        >
                            {siteBuilderIconGlyph(firstBlockText(block, ["icon"], "lucide:check"))}
                        </span>
                        <span
                            {...siteBuilderEditorPartAttributes({
                                editorMode: runtime.editorMode,
                                inlineText: true,
                                part: "items",
                            })}
                            {...siteBuilderEditorInlinePlaceholderAttributes({
                                block,
                                editorMode: runtime.editorMode,
                                empty: items.length === 0,
                            })}
                            data-builder-icon-list-text={runtime.editorMode ? "true" : undefined}
                            style={partResizeStyleRecord(block, "items")}
                        >
                            {item}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }

    if (block.type === "accordionItem") {
        const titleKey = block.content?.title !== undefined ? "title" : "question";
        const textKey = block.content?.text !== undefined ? "text" : "answer";
        const iconMode = blockOptionText(block, "accordionIconMode", "arrow");
        const closedIcon = iconMode === "plusMinus"
            ? "plus"
            : blockOptionText(block, "accordionClosedIcon", "chevronRight");
        const openIcon = iconMode === "plusMinus"
            ? "minus"
            : blockOptionText(block, "accordionOpenIcon", "chevronDown");
        const transition = blockOptionText(block, "accordionTransition", "slide");
        const styleVariant = blockOptionText(block, "accordionStyle", "boxed");

        return (
            <details
                {...commonProps}
                className={[
                    "site-builder-accordion-item",
                    `is-style-${styleVariant}`,
                    `is-transition-${transition}`,
                ].join(" ")}
                data-builder-accordion-part={runtime.editorMode ? path : undefined}
                style={styleRecord(block)}
            >
                <summary data-builder-accordion-toggle={runtime.editorMode ? "true" : undefined}>
                    <span
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: titleKey,
                        })}
                        {...siteBuilderEditorInlinePlaceholderAttributes({
                            block,
                            editorMode: runtime.editorMode,
                            empty: inlineTextIsEmpty(block, titleKey),
                        })}
                        data-builder-accordion-title={runtime.editorMode ? "true" : undefined}
                        style={partResizeStyleRecord(block, titleKey)}
                    >
                        {richText(block, titleKey, runtime)}
                    </span>
                    <span
                        aria-hidden="true"
                        className="site-builder-accordion-icon site-builder-accordion-icon-closed"
                        contentEditable={false}
                    >
                        {siteBuilderIconGlyph(closedIcon)}
                    </span>
                    <span
                        aria-hidden="true"
                        className="site-builder-accordion-icon site-builder-accordion-icon-open"
                        contentEditable={false}
                    >
                        {siteBuilderIconGlyph(openIcon)}
                    </span>
                </summary>
                <p
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: textKey,
                    })}
                    {...siteBuilderEditorInlinePlaceholderAttributes({
                        block,
                        editorMode: runtime.editorMode,
                        empty: inlineTextIsEmpty(block, textKey),
                    })}
                    style={partResizeStyleRecord(block, textKey)}
                >
                    {richText(block, textKey, runtime)}
                </p>
            </details>
        );
    }

    if (block.type === "accordion") {
        const slotItems = blocksInSlot(block, "content");
        const items = Array.isArray(block.content?.items) ? block.content.items : [];

        return (
            <div
                {...commonProps}
                className="site-builder-accordion"
                data-builder-accordion-part={runtime.editorMode ? path : undefined}
                style={styleRecord(block)}
            >
                <div
                    {...siteBuilderEditorSlotAttributes({
                        editorMode: runtime.editorMode,
                        itemCount: slotItems.length || items.length,
                        slot: "content",
                    })}
                    data-builder-empty-slot={
                        runtime.editorMode
                        && slotItems.length === 0
                        && items.length === 0
                            ? "true"
                            : undefined
                    }
                    style={{
                        display: "grid",
                        gap: styleRecord(block).gap,
                    }}
                >
                    {slotItems.length > 0 ? slotItems.map((child, index) => (
                        <SiteBuilderBlockView
                            key={`${child.uuid ?? child.type}:${index}`}
                            block={{
                                ...child,
                                options: {
                                    ...block.options,
                                    ...child.options,
                                },
                            }}
                            parentType={block.type}
                            path={siteBuilderEditorChildPath(path, "content", child, index)}
                            runtime={runtime}
                            slot="content"
                        />
                    )) : items.map((item, index) => {
                        const value = record(item);
                        const part = `content:${index}`;

                        return (
                            <details
                                className={[
                                    "site-builder-accordion-item",
                                    `is-style-${blockOptionText(block, "accordionStyle", "boxed")}`,
                                    `is-transition-${blockOptionText(block, "accordionTransition", "slide")}`,
                                ].join(" ")}
                                data-builder-accordion-part={runtime.editorMode ? part : undefined}
                                key={`${textValue(value.title)}:${index}`}
                                style={partResizeStyleRecord(block, part)}
                            >
                                <summary data-builder-accordion-toggle={runtime.editorMode ? "true" : undefined}>
                                    <span
                                        {...siteBuilderEditorPartAttributes({
                                            editorMode: runtime.editorMode,
                                            inlineText: true,
                                            part: `${part}:title`,
                                        })}
                                        data-builder-accordion-title={runtime.editorMode ? "true" : undefined}
                                        style={partResizeStyleRecord(block, `${part}:title`)}
                                    >
                                        {textValue(value.title, textValue(value.question))}
                                    </span>
                                    <span
                                        aria-hidden="true"
                                        className="site-builder-accordion-icon site-builder-accordion-icon-closed"
                                    >
                                        {siteBuilderIconGlyph(
                                            blockOptionText(block, "accordionIconMode", "arrow") === "plusMinus"
                                                ? "plus"
                                                : blockOptionText(block, "accordionClosedIcon", "chevronRight"),
                                        )}
                                    </span>
                                    <span
                                        aria-hidden="true"
                                        className="site-builder-accordion-icon site-builder-accordion-icon-open"
                                    >
                                        {siteBuilderIconGlyph(
                                            blockOptionText(block, "accordionIconMode", "arrow") === "plusMinus"
                                                ? "minus"
                                                : blockOptionText(block, "accordionOpenIcon", "chevronDown"),
                                        )}
                                    </span>
                                </summary>
                                <p
                                    {...siteBuilderEditorPartAttributes({
                                        editorMode: runtime.editorMode,
                                        inlineText: true,
                                        part: `${part}:text`,
                                    })}
                                    style={partResizeStyleRecord(block, `${part}:text`)}
                                >
                                    {textValue(value.text, textValue(value.answer))}
                                </p>
                            </details>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (block.type === "blockquote") {
        const quoteKey = block.content?.quote !== undefined ? "quote" : "text";
        const quoteContent = richText(block, quoteKey, runtime);
        const shape = blockOptionText(block, "blockquoteShape", "none");
        const decoration = blockOptionText(block, "blockquoteDecoration", "none");
        const showAuthor = optionalBooleanValue(blockOption(block, "showAuthor")) !== false;
        const authorAlignment = blockOptionText(block, "authorAlignment", "left");
        const imageUrl = firstBlockText(block, ["imageUrl", "image", "src"]);
        const shapeStyle: StyleRecord = shape === "slant"
            ? { clipPath: "polygon(3% 0, 100% 0, 97% 100%, 0 100%)" }
            : shape === "wave"
                ? { clipPath: "polygon(0 4%, 25% 0, 50% 4%, 75% 0, 100% 4%, 100% 96%, 75% 100%, 50% 96%, 25% 100%, 0 96%)" }
                : shape === "jagged"
                    ? { clipPath: "polygon(0 6%, 4% 0, 9% 5%, 14% 0, 20% 5%, 26% 0, 33% 5%, 40% 0, 48% 5%, 56% 0, 64% 5%, 72% 0, 81% 5%, 90% 0, 100% 6%, 97% 50%, 100% 94%, 90% 100%, 81% 95%, 72% 100%, 64% 95%, 56% 100%, 48% 95%, 40% 100%, 33% 95%, 26% 100%, 20% 95%, 14% 100%, 9% 95%, 4% 100%, 0 94%, 3% 50%)" }
                    : shape === "arch"
                        ? { borderRadius: "999px 999px var(--site-radius, 16px) var(--site-radius, 16px)" }
                        : shape === "rounded"
                            ? { borderRadius: "var(--site-radius, 16px)" }
                            : {};
        const configured = styleRecord(block);

        return (
            <blockquote
                {...commonProps}
                className="site-builder-blockquote"
                data-site-builder-blockquote-decoration={decoration}
                data-site-builder-blockquote-shape={shape}
                style={{
                    ...shapeStyle,
                    ...configured,
                    borderLeftColor: decoration === "leftLine"
                        ? configured.borderColor ?? "var(--site-primary)"
                        : configured.borderLeftColor,
                    borderLeftStyle: decoration === "leftLine"
                        ? "solid"
                        : configured.borderLeftStyle,
                    borderLeftWidth: decoration === "leftLine"
                        ? configured.borderWidth ?? "4px"
                        : configured.borderLeftWidth,
                    padding: configured.padding
                        ?? cssLength(blockOption(block, "edgeSpacing")),
                    width: configured.width
                        ?? (blockOptionText(block, "blockquoteWidth") || undefined),
                }}
            >
                {decoration === "icon" && blocksInSlot(block, "icon").length === 0 ? (
                    <span aria-hidden="true" className="site-builder-blockquote-decoration">
                        “
                    </span>
                ) : null}
                <span
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: quoteKey,
                    })}
                    {...siteBuilderEditorInlinePlaceholderAttributes({
                        block,
                        editorMode: runtime.editorMode,
                        empty: inlineTextIsEmpty(block, quoteKey),
                    })}
                    style={{
                        display: "block",
                        ...partResizeStyleRecord(block, quoteKey),
                    }}
                >
                    {quoteContent}
                </span>
                {showAuthor && blockText(block, "author") ? (
                    <cite
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: "author",
                        })}
                        style={{
                            display: "block",
                            textAlign: authorAlignment === "center"
                                ? "center"
                                : authorAlignment === "right"
                                    ? "right"
                                    : "left",
                            ...partResizeStyleRecord(block, "author"),
                        }}
                    >
                        {richText(block, "author", runtime)}
                    </cite>
                ) : null}
                {imageUrl ? (
                    <img
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            part: "image",
                        })}
                        alt={blockText(block, "alt")}
                        className="site-builder-blockquote-image"
                        src={imageUrl}
                        style={{
                            display: "block",
                            maxWidth: "100%",
                            ...partResizeStyleRecord(block, "image"),
                        }}
                    />
                ) : null}
                {nestedBlocks(block, runtime, path)}
            </blockquote>
        );
    }

    if (block.type === "rating") {
        const { max, value } = siteBuilderRatingPresentation(block);
        const roundedValue = Math.round(value);
        const titlePosition = blockOptionText(block, "ratingTitlePosition", "left");
        const score = `${Number.isInteger(value) ? value : value.toFixed(1)} / ${max}`;
        const title = blockText(block, "title");
        const icons = (
            <span
                aria-hidden="true"
                className="site-builder-rating-icons"
                style={{
                    alignItems: "center",
                    color: blockOptionText(block, "ratingIconColor") || undefined,
                    display: "inline-flex",
                    fontSize: ratingFontSize(block),
                    gap: ".18em",
                    lineHeight: 1,
                }}
            >
                {Array.from({ length: max }, (_, index) => (
                    <span
                        data-site-builder-rating-active={index < roundedValue ? "true" : "false"}
                        key={index}
                        style={{ opacity: index < roundedValue ? 1 : .35 }}
                    >
                        {ratingGlyph(block, index < roundedValue)}
                    </span>
                ))}
            </span>
        );

        return (
            <div
                {...commonProps}
                aria-label={score}
                className="site-builder-rating"
                data-site-builder-rating-icon={blockOptionText(block, "ratingIcon", "stars")}
                style={{
                    alignItems: titlePosition === "top" ? "flex-start" : "center",
                    display: "flex",
                    flexDirection: titlePosition === "top" ? "column" : "row",
                    gap: titlePosition === "top" ? ".45rem" : ".75rem",
                    ...styleRecord(block),
                }}
            >
                {titlePosition === "none" ? null : (
                    <span
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: Boolean(title),
                            part: title ? "title" : "value",
                        })}
                        className="site-builder-rating-score"
                        style={{
                            fontWeight: 700,
                            ...partResizeStyleRecord(
                                block,
                                title ? "title" : "value",
                            ),
                        }}
                    >
                        {title ? richText(block, "title", runtime) : score}
                    </span>
                )}
                {icons}
            </div>
        );
    }

    if (block.type === "progressBar") {
        const value = siteBuilderProgressValue(block);
        const position = blockOptionText(block, "progressLabelPosition", "inside");
        const label = firstBlockText(block, ["title", "label"]);
        const striped = booleanValue(blockOption(block, "progressStriped"));
        const labelNode = position === "hide" ? null : (
            <span
                className="site-builder-progress-label"
                style={{
                    color: blockOptionText(block, "progressLabelColor") || undefined,
                }}
            >
                {label ? (
                    <span
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: block.content?.title !== undefined
                                ? "title"
                                : "label",
                        })}
                        style={partResizeStyleRecord(
                            block,
                            block.content?.title !== undefined
                                ? "title"
                                : "label",
                        )}
                    >
                        {richText(
                            block,
                            block.content?.title !== undefined
                                ? "title"
                                : "label",
                            runtime,
                            label,
                        )}
                    </span>
                ) : null}
                {label ? " · " : null}
                <span className="site-builder-progress-value">
                    {Math.round(value)}%
                </span>
            </span>
        );

        return (
            <div
                {...commonProps}
                className="site-builder-progress"
                style={{
                    alignItems: position === "after" ? "center" : undefined,
                    display: position === "after" ? "flex" : "grid",
                    gap: ".45rem",
                    ...styleRecord(block),
                }}
            >
                {position === "inside" || position === "below" || position === "after"
                    ? null
                    : labelNode}
                <div
                    aria-label={label || `${Math.round(value)}%`}
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={value}
                    className="site-builder-progress-track"
                    role="progressbar"
                    style={{
                        backgroundColor: blockOptionText(block, "progressTrackColor", "var(--site-border)"),
                        borderRadius: "9999px",
                        flex: position === "after" ? 1 : undefined,
                        minHeight: "1.5rem",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <span
                        className="site-builder-progress-fill"
                        style={{
                            alignItems: "center",
                            backgroundColor: blockOptionText(block, "progressFillColor", "var(--site-primary)"),
                            backgroundImage: striped
                                ? "linear-gradient(45deg, rgb(255 255 255 / .18) 25%, transparent 25%, transparent 50%, rgb(255 255 255 / .18) 50%, rgb(255 255 255 / .18) 75%, transparent 75%)"
                                : undefined,
                            backgroundSize: striped ? "1rem 1rem" : undefined,
                            display: "flex",
                            height: "100%",
                            justifyContent: "center",
                            minHeight: "1.5rem",
                            width: `${value}%`,
                        }}
                    >
                        {position === "inside" ? labelNode : null}
                    </span>
                </div>
                {position === "below" || position === "after" ? labelNode : null}
            </div>
        );
    }

    if (block.type === "code") {
        const codeValue = Object.prototype.hasOwnProperty.call(
            block.content ?? {},
            "code",
        )
            ? blockFieldText(block, "content", "code")
            : firstBlockText(block, ["text"]);
        const source = siteEmbedSource(codeValue);
        const aspectRatio = siteBuilderAspectRatio(
            blockOptionText(block, "embedAspectRatio", "wide"),
        ) ?? "16 / 9";
        const configuredStyle = styleRecord(block);

        if (source) {
            return (
                <iframe
                    {...commonProps}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="site-builder-code-embed"
                    data-site-builder-code-embed="true"
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    sandbox="allow-scripts allow-same-origin allow-presentation"
                    src={source}
                    style={{
                        ...configuredStyle,
                        aspectRatio,
                        height: configuredStyle.height ?? "auto",
                        minHeight: configuredStyle.minHeight ?? 0,
                        pointerEvents: runtime.editorMode ? "none" : undefined,
                        width: configuredStyle.width ?? "100%",
                    }}
                    tabIndex={runtime.editorMode ? -1 : undefined}
                    title={blockText(block, "title", "Embedded content")}
                />
            );
        }

        return (
            <pre {...commonProps} className="site-builder-code" style={configuredStyle}>
                <code
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: block.content?.code !== undefined ? "code" : "text",
                    })}
                    {...siteBuilderEditorInlinePlaceholderAttributes({
                        block,
                        editorMode: runtime.editorMode,
                        empty: firstBlockText(block, ["code", "text"]).trim() === "",
                    })}
                    style={partResizeStyleRecord(
                        block,
                        block.content?.code !== undefined ? "code" : "text",
                    )}
                >
                    {codeValue}
                </code>
            </pre>
        );
    }

    if (block.type === "textHighlight") {
        const configuredStyle = styleRecord(block);
        const width = cssLength(blockOption(block, "textHighlightWidth"));
        const alignment = blockFieldText(block, "options", "alignment");
        const alignedMargins = alignment === "center"
            ? { marginLeft: "auto", marginRight: "auto" }
            : alignment === "right" || alignment === "end"
                ? { marginLeft: "auto", marginRight: 0 }
                : alignment === "left" || alignment === "start"
                    ? { marginLeft: 0, marginRight: "auto" }
                    : {};

        return (
            <div
                {...commonProps}
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: "text",
                })}
                {...siteBuilderEditorInlinePlaceholderAttributes({
                    block,
                    editorMode: runtime.editorMode,
                    empty: inlineTextIsEmpty(block, "text"),
                })}
                className="site-builder-textHighlight"
                data-site-builder-text-highlight-width={width}
                style={{
                    ...configuredStyle,
                    ...(width ? {
                        maxWidth: configuredStyle.maxWidth ?? width,
                        width: configuredStyle.width ?? width,
                    } : {}),
                    ...alignedMargins,
                    ...partResizeStyleRecord(block, "text"),
                }}
            >
                {richText(block, "text", runtime, firstBlockText(block, ["title", "label"]))}
                {nestedBlocks(block, runtime, path)}
            </div>
        );
    }

    if (block.type === "alert") {
        return (
            <div
                {...commonProps}
                {...siteBuilderEditorPartAttributes({
                    editorMode: runtime.editorMode,
                    inlineText: true,
                    part: "text",
                })}
                {...siteBuilderEditorInlinePlaceholderAttributes({
                    block,
                    editorMode: runtime.editorMode,
                    empty: inlineTextIsEmpty(block, "text"),
                })}
                className={`site-builder-${block.type}`}
                style={{
                    ...styleRecord(block),
                    ...partResizeStyleRecord(block, "text"),
                }}
            >
                {richText(block, "text", runtime, firstBlockText(block, ["title", "label"]))}
                {nestedBlocks(block, runtime, path)}
            </div>
        );
    }

    if (block.type === "avatars") {
        const names = blockFieldText(block, "content", "items")
            .split("\n")
            .filter(Boolean);
        const layout = blockOptionText(block, "avatarLayout", "stack");
        const showNames = booleanValue(blockOption(block, "showAvatarNames"));
        const title = blockText(block, "title");

        return (
            <div
                {...commonProps}
                className={`site-builder-avatars site-builder-avatars-${layout}`}
                style={{
                    display: "grid",
                    gap: ".65rem",
                    ...styleRecord(block),
                }}
            >
                {title ? (
                    <strong
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: "title",
                        })}
                        style={partResizeStyleRecord(block, "title")}
                    >
                        {richText(block, "title", runtime)}
                    </strong>
                ) : null}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: layout === "pills" ? ".5rem" : 0,
                    }}
                >
                    {names.map((name, index) => (
                        <span
                            className="site-builder-avatar"
                            key={`${name}:${index}`}
                            style={{
                                alignItems: "center",
                                backgroundColor: "var(--site-surface)",
                                border: "2px solid var(--site-background, #fff)",
                                borderRadius: "9999px",
                                display: "inline-flex",
                                gap: ".4rem",
                                marginLeft: layout === "stack" && index > 0 ? "-.65rem" : 0,
                                minHeight: "2.5rem",
                                minWidth: "2.5rem",
                                padding: showNames ? ".35rem .7rem .35rem .45rem" : ".35rem",
                                position: "relative",
                            }}
                            title={name}
                        >
                            <b
                                aria-hidden="true"
                                style={{
                                    alignItems: "center",
                                    display: "inline-flex",
                                    height: "1.8rem",
                                    justifyContent: "center",
                                    width: "1.8rem",
                                }}
                            >
                                {siteBuilderAvatarInitials(name)}
                            </b>
                            {showNames ? <span>{name}</span> : null}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    if (block.type === "supportedPaymentButton") {
        const title = blockText(block, "title");
        const items = firstBlockText(block, ["items", "text"])
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean);
        const layout = blockOptionText(block, "paymentLayout", "cards");
        const showLabels = optionalBooleanValue(blockOption(block, "showLabels")) !== false;

        return (
            <div
                {...commonProps}
                className={`site-builder-payments site-builder-payments-${layout}`}
                style={styleRecord(block)}
            >
                {title ? (
                    <strong
                        {...siteBuilderEditorPartAttributes({
                            editorMode: runtime.editorMode,
                            inlineText: true,
                            part: "title",
                        })}
                        style={partResizeStyleRecord(block, "title")}
                    >
                        {richText(block, "title", runtime)}
                    </strong>
                ) : null}
                <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
                    {items.map((item) => (
                        <span
                            key={item}
                            style={{
                                border: "1px solid var(--site-border)",
                                borderRadius: layout === "compact" ? "6px" : "10px",
                                padding: layout === "compact" ? ".35rem .55rem" : ".55rem .75rem",
                            }}
                        >
                            {showLabels ? item : item.slice(0, 2).toUpperCase()}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    if (block.type === "countdown") {
        const targetDate = firstBlockText(block, ["targetDate", "date", "label", "text"]);

        return (
            <div {...commonProps}>
                <SiteBuilderCountdown
                    languageCode={
                        runtime.page?.languageCode
                        ?? runtime.site?.defaultLanguageCode
                        ?? runtime.pages[0]?.languageCode
                        ?? "en"
                    }
                    outlineOwner={runtime.editorMode}
                    style={styleRecord(block)}
                    targetDate={targetDate}
                    title={richText(block, "title", runtime)}
                    titleAttributes={siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: "title",
                    })}
                    titleStyle={partResizeStyleRecord(block, "title")}
                />
            </div>
        );
    }

    if (block.type === "searchBar") {
        const placeholder = firstBlockText(block, ["placeholder", "label"], "Search");
        const suggestions = siteBuilderSearchSuggestions(
            runtime.pages,
            runtime.site,
            blockOptionText(block, "searchWithin", "everything"),
            blockOption(block, "searchResultLimit"),
        );
        const listId = `site-builder-search-${(block.uuid ?? path).replace(/[^a-z0-9_-]/giu, "-")}`;
        const buttonLabel = blockText(block, "buttonLabel");

        return (
            <SiteBuilderSearchForm
                {...commonProps}
                className="site-builder-search"
                editorMode={runtime.editorMode}
                languageCode={
                    runtime.page?.languageCode
                    ?? runtime.site?.defaultLanguageCode
                    ?? runtime.pages[0]?.languageCode
                    ?? "en"
                }
                method="get"
                readOnly={runtime.readOnly}
                style={{
                    alignItems: "center",
                    display: "flex",
                    ...styleRecord(block),
                }}
                suggestions={suggestions}
            >
                {nestedBlocks(block, runtime, path)}
                <input
                    aria-label={placeholder}
                    list={suggestions.length > 0 ? listId : undefined}
                    name={runtime.editorMode ? undefined : "q"}
                    placeholder={placeholder}
                    readOnly={runtime.editorMode || runtime.readOnly}
                    style={{
                        background: "transparent",
                        border: 0,
                        color: "inherit",
                        flex: 1,
                        font: "inherit",
                        minWidth: 0,
                        outline: 0,
                    }}
                    type="search"
                />
                {suggestions.length > 0 ? (
                    <datalist id={listId}>
                        {suggestions.map((item) => (
                            <option key={`${item.href}:${item.label}`} value={item.label}>
                                {item.href}
                            </option>
                        ))}
                    </datalist>
                ) : null}
                {buttonLabel ? (
                    <button
                        disabled={runtime.editorMode || runtime.readOnly}
                        type="submit"
                    >
                        <span
                            {...siteBuilderEditorPartAttributes({
                                editorMode: runtime.editorMode,
                                inlineText: true,
                                part: "buttonLabel",
                            })}
                            style={partResizeStyleRecord(block, "buttonLabel")}
                        >
                            {richText(block, "buttonLabel", runtime)}
                        </span>
                    </button>
                ) : null}
            </SiteBuilderSearchForm>
        );
    }

    if (block.type === "chart") {
        const textKey = block.content?.title !== undefined ? "title" : "text";
        const series = chartSeries(block);
        const rows = chartRows(block, series.length);
        const legendPosition = blockOptionText(block, "chartLegend", "top");
        const type = blockOptionText(block, "chartType", "barVertical");
        const legendItems = type === "pie" || type === "doughnut"
            ? rows.map((row, index) => {
                const color = SITE_BUILDER_CHART_COLORS[index % SITE_BUILDER_CHART_COLORS.length] ?? "var(--site-primary)";

                return {
                    borderColor: color,
                    color,
                    label: row.label,
                };
            })
            : series;
        const legend = legendPosition === "none" ? null : (
            <div
                className="site-builder-chart-legend"
                data-site-builder-chart-legend={legendPosition}
                style={{
                    display: "flex",
                    flexDirection: legendPosition === "left" || legendPosition === "right" ? "column" : "row",
                    flexWrap: "wrap",
                    gap: ".5rem 1rem",
                }}
            >
                {legendItems.map((item, index) => (
                    <span
                        key={`${item.label}:${index}`}
                        style={{ alignItems: "center", display: "inline-flex", fontSize: ".8rem", gap: ".4rem" }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                backgroundColor: item.color,
                                border: `1px solid ${item.borderColor}`,
                                borderRadius: "9999px",
                                height: ".65rem",
                                width: ".65rem",
                            }}
                        />
                        {item.label}
                    </span>
                ))}
            </div>
        );
        const sideLegend = legendPosition === "left" || legendPosition === "right";

        return (
            <div
                {...commonProps}
                className="site-builder-chart"
                data-site-builder-chart-type={type}
                data-site-builder-chart-tooltip={booleanValue(blockOption(block, "chartTooltip")) ? "true" : "false"}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    minHeight: "280px",
                    minWidth: 0,
                    width: "100%",
                    ...styleRecord(block),
                }}
            >
                <h3
                    {...siteBuilderEditorPartAttributes({
                        editorMode: runtime.editorMode,
                        inlineText: true,
                        part: textKey,
                    })}
                    className="site-builder-chart-title"
                    style={{
                        fontSize: "clamp(1.25rem, 3vw, 2rem)",
                        fontWeight: 800,
                        lineHeight: 1.1,
                        margin: 0,
                        ...partResizeStyleRecord(block, textKey),
                    }}
                >
                    {richText(block, textKey, runtime, firstBlockText(block, ["title", "text"]))}
                </h3>
                {nestedBlocks(block, runtime, path)}
                <div
                    className="site-builder-chart-body"
                    style={{
                        alignItems: sideLegend ? "center" : "stretch",
                        display: "flex",
                        flex: 1,
                        flexDirection: sideLegend ? "row" : "column",
                        gap: "1rem",
                        minHeight: 0,
                    }}
                >
                    {legendPosition === "left" || legendPosition === "top" ? legend : null}
                    <div
                        className="site-builder-chart-graphic"
                        style={{
                            display: "grid",
                            flex: 1,
                            minHeight: "220px",
                            minWidth: 0,
                            placeItems: "center",
                        }}
                    >
                        {chartGraphic(block, rows, series)}
                    </div>
                    {legendPosition === "right" || legendPosition === "bottom" ? legend : null}
                </div>
            </div>
        );
    }

    return (
        <div {...commonProps} className="site-builder-unknown" style={styleRecord(block)}>
            {nestedBlocks(block, runtime, path)}
        </div>
    );
}

export function siteBuilderBlocks(contentJson: SiteBuilderContentJson | null | undefined): SiteBuilderBlock[] {
    return Array.isArray(contentJson?.blocks) ? contentJson.blocks : [];
}

export function hasSiteBuilderBlocks(contentJson: SiteBuilderContentJson | null | undefined): boolean {
    return siteBuilderBlocks(contentJson).length > 0;
}

export function SiteBuilderContent({
    bookingDemoData = false,
    contentJson,
    design,
    editorMode = false,
    headerActions,
    LinkComponent,
    menus = [],
    navigationReadOnly,
    page,
    pages,
    readOnly,
    renderForm,
    site,
}: {
    bookingDemoData?: boolean;
    contentJson: SiteBuilderContentJson | null | undefined;
    design: ResolvedSiteBuilderDesign;
    editorMode?: boolean;
    headerActions?: ReactNode;
    LinkComponent: SiteBuilderLinkComponent;
    menus?: readonly SiteNavigationMenu[];
    navigationReadOnly?: boolean;
    page: SiteBuilderRuntimePage | null;
    pages: SiteBuilderRuntimePageLink[];
    readOnly?: boolean;
    renderForm?: SiteBuilderFormRenderer;
    site: SiteBuilderRuntimeSite | null;
}) {
    const runtime: SiteBuilderRuntime = {
        bookingDemoData,
        design,
        editorMode,
        headerActions,
        LinkComponent,
        menus,
        navigationReadOnly: navigationReadOnly ?? readOnly ?? false,
        page,
        pages,
        readOnly,
        renderForm,
        site,
    };

    const renderedContent = (
        <>
            <SiteBuilderInteractionRuntime />
            <style data-site-builder-interaction-styles="true">
                {SITE_BUILDER_INTERACTION_CSS}
            </style>
            {siteBuilderBlocks(contentJson).map((block, index) => (
                <SiteBuilderBlockView
                    key={`${block.uuid ?? block.type}:${index}`}
                    block={block}
                    path={siteBuilderEditorRootPath(block, index)}
                    runtime={runtime}
                />
            ))}
        </>
    );

    return hasBookingPortalBlock(contentJson) ? (
        <div
            className="site-builder-booking-portal-page"
            data-site-builder-editor-mode={runtime.editorMode ? "true" : undefined}
            data-site-builder-booking-portal-page="true"
        >
            {renderedContent}
        </div>
    ) : renderedContent;
}
